<template>
  <div class="post-detail-page">
    <div class="container post-container">
      <!-- 文章標籤 -->
      <div class="mb-3">
        <span class="badge" :class="article.category === 'news' ? 'bg-primary' : 'bg-success'">
          {{ categoryName }}
        </span>
        <span class="text-muted ms-3">{{ article.date }}</span>
      </div>

      <!-- 文章標題 -->
      <h1 class="article-title mb-4">{{ article.title }}</h1>

      <!-- 文章內容 -->
      <div class="article-content" v-html="article.content"></div>

      <!-- 上一篇/下一篇導覽 -->
      <div class="article-navigation mt-5 pt-4">
        <div class="row">
          <div class="col-md-6 mb-3" v-if="prevArticle">
            <router-link :to="`/post/${prevArticle.slug}`" class="nav-article-link prev-article">
              <div class="nav-label">← 上一篇</div>
              <div class="nav-title">{{ prevArticle.title }}</div>
            </router-link>
          </div>
          <div class="col-md-6 mb-3" v-if="nextArticle">
            <router-link :to="`/post/${nextArticle.slug}`" class="nav-article-link next-article">
              <div class="nav-label">下一篇 →</div>
              <div class="nav-title">{{ nextArticle.title }}</div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- 延伸閱讀 -->
      <div class="related-articles mt-5 pt-5">
        <h3 class="mb-4">延伸閱讀</h3>
        <div class="related-list">
          <router-link
            v-for="related in relatedArticles"
            :key="related.slug"
            :to="`/post/${related.slug}`"
            class="related-item"
          >
            <div class="related-item-content">
              <div class="related-badge-date">
                <span class="badge" :class="related.category === 'news' ? 'bg-primary' : 'bg-success'">
                  {{ related.category === 'news' ? '新聞總覽' : '知識百科' }}
                </span>
                <span class="text-muted ms-3">{{ related.date }}</span>
              </div>
              <h5 class="related-title mt-2">{{ related.title }}</h5>
            </div>
          </router-link>
        </div>
      </div>

      <!-- 返回按鈕 -->
      <div class="text-center mt-5">
        <router-link to="/lesson" class="btn btn-outline-primary btn-lg">
          ← 返回地產小學堂
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useHead } from '@vueuse/head';
import { useRoute } from 'vue-router';

export default {
  name: 'PostDetail',
  setup() {
    const route = useRoute();
    
    // 動態設置 meta 標籤
    const articleTitle = computed(() => {
      return route.meta.articleTitle || '文章詳情 | 地產小學堂 - 長發地產';
    });
    
    const articleDescription = computed(() => {
      return route.meta.articleDescription || '長發地產提供專業房地產知識與最新開發資訊';
    });
    
    // 添加Article和BreadcrumbList結構化數據
    const articleSlug = computed(() => route.params.slug);
    
    useHead({
      title: articleTitle,
      meta: [
        {
          name: 'description',
          content: articleDescription
        },
        { property: 'og:type', content: 'article' },
        { property: 'og:title', content: articleTitle },
        { property: 'og:description', content: articleDescription },
        { name: 'twitter:card', content: 'summary_large_image' },
        { property: 'article:author', content: '長發地產' },
        { property: 'article:section', content: '地產小學堂' }
      ],
      script: [
        // BreadcrumbList 麵包屑導航
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首頁',
                item: 'https://changfarealestate.com.tw/'
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '地產小學堂',
                item: 'https://changfarealestate.com.tw/lesson'
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: articleTitle.value
              }
            ]
          })
        },
        // Article 結構化數據
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: articleTitle.value,
            description: articleDescription.value,
            author: {
              '@type': 'Organization',
              name: '長發地產',
              url: 'https://changfarealestate.com.tw'
            },
            publisher: {
              '@type': 'Organization',
              name: '長發地產',
              logo: {
                '@type': 'ImageObject',
                url: 'https://changfarealestate.com.tw/長發外觀.jpg'
              }
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://changfarealestate.com.tw/post/${articleSlug.value}`
            }
          })
        }
      ]
    });
  },
  data() {
    return {
      articlesList: [
        // 知識百科
        { slug: 'knowledge-1', category: 'wiki', date: '2024.10.15', title: '什麼是區段徵收？' },
        { slug: 'knowledge-2', category: 'wiki', date: '2024.10.10', title: '容積率與建蔽率怎麼算？' },
        { slug: 'knowledge-3', category: 'wiki', date: '2024.10.08', title: '都市更新與危老重建差異' },
        { slug: 'knowledge-4', category: 'wiki', date: '2024.09.25', title: '土地使用分區與地目說明' },
        { slug: 'knowledge-5', category: 'wiki', date: '2024.09.18', title: '土地增值稅如何計算？' },
        { slug: 'knowledge-6', category: 'wiki', date: '2024.09.12', title: '不動產實價登錄查詢技巧' },
        { slug: 'knowledge-7', category: 'wiki', date: '2024.09.05', title: '農地買賣注意事項' },
        { slug: 'knowledge-8', category: 'wiki', date: '2024.08.28', title: '地上權與所有權的差別' },
        { slug: 'knowledge-9', category: 'wiki', date: '2024.08.20', title: '重劃與徵收的區別' },
        // 新聞總覽
        { slug: 'news-18', category: 'news', date: '2026.05.27', title: '三鶯線延伸八德工程啟動 張善政：123年朝通車目標邁進' },
        { slug: 'news-17', category: 'news', date: '2026.05.27', title: '🚀桃園航空城95公頃產專區｜正式進入「精準招商」階段‼️' },
        { slug: 'news-16', category: 'news', date: '2026.03.15', title: "區段徵收G12、G13、G14 開發案『市都委會第一次審議進度解析』" },
        { slug: 'news-10', category: 'news', date: '2025.11.18', title: '桃園航空城優先產業專用區 I 基地開發計畫 - 台壽、榮運攜手打造國際級巨蛋與產業城' },
        { slug: 'news-11', category: 'news', date: '2025.11.17', title: '經國特區好消息！幸福路跨南崁溪「人行跨橋」正式啟動' },
        { slug: 'news-12', category: 'news', date: '2025.11.14', title: '經國特區 × G12 G13 G14｜啟動桃園黃金10年' },
        { slug: 'news-13', category: 'news', date: '2025.11.13', title: '桃園重大產業用地標售｜桃科二期 46,682 坪正式釋出' },
        { slug: 'news-14', category: 'news', date: '2025.11.06', title: '桃園建設大紀事｜國道2號「大竹交流道改善工程」正式動土' },
        { slug: 'news-15', category: 'news', date: '2025.11.03', title: '桃園航空城｜又一重大工程正式啟動招標！' },
        { slug: 'news-1', category: 'news', date: '2025.10.19', title: '結合航空城！桃園蘆竹誠聖段800戶社宅決標 2027年動工' },
        { slug: 'news-2', category: 'news', date: '2025.10.20', title: '航空城拆遷戶貸款卡關，是被信用管制措施害慘了？央行最新回應出爐' },
        { slug: 'news-3', category: 'news', date: '2025.02.25', title: '桃園航空城計畫重大進度曝！「先建後遷」期程出爐、不再展延' },
        { slug: 'news-4', category: 'news', date: '2024.09.06', title: '桃園捷運綠線G12站土地開發計畫啟動' },
        { slug: 'news-5', category: 'news', date: '2024.09.06', title: '桃園捷運綠線機廠工程進度超前' },
        { slug: 'news-6', category: 'news', date: '2024.09.05', title: '台鐵中路站周邊都市計畫啟動，124公頃土地開發' },
        { slug: 'news-7', category: 'news', date: '2025.01.06', title: '桃園都市更新獎勵加碼，防災型重建最高獎勵容積50%' },
        { slug: 'news-8', category: 'news', date: '2024.08.20', title: '桃園小檜溪重劃區房價飆漲，吸引首購族進駐' },
        { slug: 'news-9', category: 'news', date: '2024.10.20', title: '桃園捷運綠線3站要開發！近千人湧入TOD說明會' }
      ],
      articles: {
        // 知識百科
        'knowledge-1': {
          category: 'wiki',
          date: '2024.10.15',
          title: '什麼是區段徵收？',
          content: `
            <h2>什麼是區段徵收？</h2>
            <p>區段徵收是政府為了新都市開發建設、舊都市更新或其他開發目的需要，對於一定區域內之土地全部予以徵收，並重新規劃整理的土地取得方式。</p>
            
            <h3>區段徵收的目的</h3>
            <ul>
              <li>新都市開發建設</li>
              <li>舊都市更新</li>
              <li>農村社區更新</li>
              <li>其他依法得徵收土地之事業</li>
            </ul>

            <h3>對土地所有權人的影響</h3>
            <p>土地所有權人可以選擇領取現金補償，或是選擇領回抵價地（配回土地）。選擇領回抵價地的地主，在開發完成後可以取得整理後的土地，通常具有較高的開發價值。</p>

            <h3>區段徵收的流程</h3>
            <ol>
              <li>發布徵收計畫</li>
              <li>地籍調查及地價評估</li>
              <li>公告徵收</li>
              <li>發放補償費或配地通知</li>
              <li>土地開發整理</li>
              <li>配地及登記</li>
            </ol>

            <p class="mt-4"><strong>了解區段徵收的相關規定，有助於保障自身權益。建議在收到徵收通知時，可諮詢專業的地政士或律師。</strong></p>
          `
        },
        'knowledge-2': {
          category: 'wiki',
          date: '2024.10.10',
          title: '容積率與建蔽率怎麼算？',
          content: `
            <h2>容積率與建蔽率怎麼算？</h2>
            <p>容積率和建蔽率是土地開發最重要的兩個指標，直接影響土地的使用價值和開發潛力。</p>

            <h3>建蔽率（Building Coverage Ratio）</h3>
            <p><strong>建蔽率 = 建築面積 ÷ 基地面積 × 100%</strong></p>
            <p>建蔽率是指建築物投影面積（建築覆蓋率）占基地面積的百分比。簡單來說，就是房子的「底面積」占土地面積的比例。</p>
            
            <h4>範例</h4>
            <p>假設有一塊100坪的土地，建蔽率為60%：<br>
            最大建築面積 = 100坪 × 60% = 60坪</p>

            <h3>容積率（Floor Area Ratio）</h3>
            <p><strong>容積率 = 總樓地板面積 ÷ 基地面積 × 100%</strong></p>
            <p>容積率是指建築物各層樓地板面積總和與基地面積的比率。容積率決定了建築物可以蓋多高、多大。</p>

            <h4>範例</h4>
            <p>假設同樣100坪的土地，容積率為300%：<br>
            總樓地板面積 = 100坪 × 300% = 300坪<br>
            如果建蔽率60%，可蓋樓層 = 300坪 ÷ 60坪 = 5層樓</p>

            <h3>不同使用分區的規定</h3>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th>使用分區</th>
                  <th>建蔽率</th>
                  <th>容積率</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>住宅區</td>
                  <td>30-60%</td>
                  <td>120-300%</td>
                </tr>
                <tr>
                  <td>商業區</td>
                  <td>55-80%</td>
                  <td>300-800%</td>
                </tr>
                <tr>
                  <td>工業區</td>
                  <td>50-70%</td>
                  <td>150-300%</td>
                </tr>
              </tbody>
            </table>

            <p class="alert alert-info mt-4">
              <strong>提醒：</strong>實際的建蔽率和容積率需依各地方政府的都市計畫規定為準。購買土地前，務必先查詢土地使用分區管制。
            </p>
          `
        },
        'knowledge-3': {
          category: 'wiki',
          date: '2024.10.08',
          title: '都市更新與危老重建差異',
          content: `
            <h2>都市更新與危老重建差異</h2>
            <p>都市更新和危老重建都是老屋改建的重要方式，但兩者在法源依據、申請門檻、審核程序和獎勵內容上有顯著差異。了解兩者差異，有助於選擇最適合的重建方案。</p>

            <h3>一、法源依據</h3>
            <ul>
              <li><strong>都市更新</strong> - 依據《都市更新條例》</li>
              <li><strong>危老重建</strong> - 依據《都市危險及老舊建築物加速重建條例》（簡稱危老條例）</li>
            </ul>

            <h3>二、申請門檻比較</h3>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="25%">項目</th>
                  <th>都市更新</th>
                  <th>危老重建</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>面積限制</strong></td>
                  <td>原則上需達1,000平方公尺以上</td>
                  <td>無面積限制，單一建物即可申請</td>
                </tr>
                <tr>
                  <td><strong>所有權人同意門檻</strong></td>
                  <td>需經土地及建物所有權人數及面積各達3/4以上同意</td>
                  <td>需全體所有權人100%同意</td>
                </tr>
                <tr>
                  <td><strong>建築物條件</strong></td>
                  <td>位於都市更新地區內，或經評估有更新必要</td>
                  <td>需符合「危險建築」或「屋齡30年以上老舊建築」</td>
                </tr>
              </tbody>
            </table>

            <h3>三、審核程序比較</h3>
            
            <h4>都市更新程序</h4>
            <ol>
              <li>劃定更新地區（或自行劃定更新單元）</li>
              <li>成立更新會或委託實施者</li>
              <li>擬定事業計畫（需公開展覽、審議）</li>
              <li>擬定權利變換計畫（需公開展覽、審議）</li>
              <li>取得建照、開工</li>
              <li>完工、分配產權</li>
              <li><strong>總時程：通常需3-7年，甚至更久</strong></li>
            </ol>

            <h4>危老重建程序</h4>
            <ol>
              <li>取得耐震評估報告（證明為危險或老舊建築）</li>
              <li>擬定重建計畫</li>
              <li>送件審查（程序簡化，無需公開展覽）</li>
              <li>核准後取得建照、開工</li>
              <li>完工、分配產權</li>
              <li><strong>總時程：約1-3年，相對快速</strong></li>
            </ol>

            <h3>四、容積獎勵比較</h3>

            <h4>都市更新獎勵</h4>
            <ul>
              <li>基準容積獎勵：最高可達50%</li>
              <li>獎勵項目多元：規模獎勵、時程獎勵、綠建築獎勵等</li>
              <li>可申請容積移轉</li>
              <li>獎勵上限：不得超過基準容積的1.5倍（部分地區可達2倍）</li>
              <li><strong>獎勵無時效限制</strong></li>
            </ul>

            <h4>危老重建獎勵</h4>
            <ul>
              <li>基本獎勵：10%容積獎勵</li>
              <li>時程獎勵：依申請時間遞減（已到期）</li>
              <li>規模獎勵：基地面積達一定規模可加碼2-4%</li>
              <li>耐震設計獎勵：最高10%</li>
              <li>智慧建築、綠建築獎勵：各最高10%</li>
              <li>獎勵上限：不得超過基準容積的1.3倍或原建築容積（擇優）</li>
              <li><strong>時程獎勵已於2023年5月到期</strong></li>
            </ul>

            <h3>五、稅賦優惠</h3>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="25%">稅目</th>
                  <th>都市更新</th>
                  <th>危老重建</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>地價稅</strong></td>
                  <td>重建期間及完工後2年減半徵收</td>
                  <td>重建期間及完工後2年減半徵收</td>
                </tr>
                <tr>
                  <td><strong>房屋稅</strong></td>
                  <td>重建期間免徵，完工後2年減半徵收</td>
                  <td>重建期間免徵，完工後2年減半徵收</td>
                </tr>
                <tr>
                  <td><strong>契稅</strong></td>
                  <td>更新後房屋減半徵收</td>
                  <td>無特別優惠</td>
                </tr>
              </tbody>
            </table>

            <h3>六、適用情境建議</h3>

            <h4>適合選擇「都市更新」的情況</h4>
            <ul>
              <li>基地面積較大，整合戶數多</li>
              <li>希望爭取較高的容積獎勵</li>
              <li>所有權人溝通不易，難以達成100%同意</li>
              <li>位於都市更新劃定地區內</li>
              <li>不急於短期內完成重建</li>
            </ul>

            <h4>適合選擇「危老重建」的情況</h4>
            <ul>
              <li>基地面積較小，甚至是單一建物</li>
              <li>所有權人單純，容易達成100%同意</li>
              <li>建築物確實有立即危險或老舊問題</li>
              <li>希望快速完成重建程序</li>
              <li>追求簡化的審核流程</li>
            </ul>

            <div class="alert alert-warning mt-4">
              <strong>⚠️ 重要提醒</strong>
              <ul class="mb-0">
                <li>危老條例的時程獎勵已於2023年5月12日到期，目前申請無時程加碼</li>
                <li>兩種方案可同時評估，但不能同時申請</li>
                <li>建議尋求專業都更顧問或建築師協助評估</li>
                <li>考量鄰居溝通意願、基地條件、時間成本等因素綜合判斷</li>
              </ul>
            </div>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">無論選擇都市更新或危老重建，建議先進行專業評估，包括建築師規劃、土地估價、財務分析等，並與所有權人充分溝通，確保重建計畫順利推動。</p>
            </div>
          `
        },
        'knowledge-4': {
          category: 'wiki',
          date: '2024.09.25',
          title: '土地使用分區與地目說明',
          content: `
            <h2>土地使用分區與地目說明</h2>
            <p>土地使用分區和地目是土地登記的兩個重要資訊，分別代表「政府規劃的用途」和「地籍登記的類別」。了解這兩項資訊，是土地交易與開發的基礎。</p>

            <h3>一、什麼是土地使用分區？</h3>
            <p>土地使用分區是依據《都市計畫法》及《區域計畫法》，由政府對土地使用所做的規劃管制，主要目的是合理分配土地資源，確保都市有序發展。</p>

            <h4>都市土地主要分區類型</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="20%">分區</th>
                  <th width="40%">用途說明</th>
                  <th width="40%">常見建蔽率/容積率</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>住宅區</strong></td>
                  <td>供建築住宅使用，並得附設日常用品零售業、服務業及其他必要設施</td>
                  <td>建蔽率：30-60%<br>容積率：120-300%</td>
                </tr>
                <tr>
                  <td><strong>商業區</strong></td>
                  <td>供商業、服務業等使用，並得附設娛樂、文教、住宅等設施</td>
                  <td>建蔽率：55-80%<br>容積率：300-800%</td>
                </tr>
                <tr>
                  <td><strong>工業區</strong></td>
                  <td>供工業使用，包括製造、加工、修理等產業</td>
                  <td>建蔽率：50-70%<br>容積率：150-300%</td>
                </tr>
                <tr>
                  <td><strong>行政區</strong></td>
                  <td>供政府機關、公共事業使用</td>
                  <td>依個案核定</td>
                </tr>
                <tr>
                  <td><strong>文教區</strong></td>
                  <td>供學校、文化機構、研究機構使用</td>
                  <td>建蔽率：40-50%<br>容積率：150-250%</td>
                </tr>
                <tr>
                  <td><strong>風景區</strong></td>
                  <td>供休閒、遊憩、觀光使用</td>
                  <td>建蔽率：通常較低<br>容積率：依景觀需求</td>
                </tr>
                <tr>
                  <td><strong>保護區</strong></td>
                  <td>國土保安、水土保持、維護天然資源與保護環境</td>
                  <td>嚴格限制開發</td>
                </tr>
                <tr>
                  <td><strong>農業區</strong></td>
                  <td>保持農業生產，並維護生態環境</td>
                  <td>農業使用，限制建築</td>
                </tr>
              </tbody>
            </table>

            <h4>住宅區的細分</h4>
            <ul>
              <li><strong>第一種住宅區</strong> - 最嚴格管制，僅允許獨棟或雙併住宅</li>
              <li><strong>第二種住宅區</strong> - 可建造連棟住宅與公寓</li>
              <li><strong>第三種住宅區</strong> - 允許較高密度住宅與部分商業設施</li>
              <li><strong>第四種住宅區</strong> - 容許高密度住宅與商業混合使用</li>
            </ul>

            <h4>非都市土地分區</h4>
            <p>非都市土地依使用分區可分為：</p>
            <ul>
              <li><strong>特定農業區</strong> - 優良農地，嚴格限制變更</li>
              <li><strong>一般農業區</strong> - 農業生產使用</li>
              <li><strong>工業區</strong> - 供工業及相關設施使用</li>
              <li><strong>鄉村區</strong> - 供鄉村居住及相關設施使用</li>
              <li><strong>森林區</strong> - 維護森林資源與生態保育</li>
              <li><strong>山坡地保育區</strong> - 水土保持與環境保護</li>
              <li><strong>風景區</strong> - 自然及文化景觀保護</li>
              <li><strong>河川區</strong> - 供河川防洪使用</li>
            </ul>

            <h3>二、什麼是地目？</h3>
            <p>地目是土地登記簿上記載的土地使用類別，反映土地歷史上的使用狀況，主要用於稅賦計算與地籍管理。</p>

            <h4>常見地目類型</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="20%">地目</th>
                  <th width="50%">說明</th>
                  <th width="30%">稅賦特性</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>建</strong>（建地）</td>
                  <td>已建築或可供建築使用的土地</td>
                  <td>地價稅較高，按一般稅率或自用住宅優惠稅率</td>
                </tr>
                <tr>
                  <td><strong>田</strong></td>
                  <td>供種植稻米等需灌溉水田作物的土地</td>
                  <td>農業使用免徵地價稅</td>
                </tr>
                <tr>
                  <td><strong>旱</strong></td>
                  <td>供種植不需灌溉作物的旱田</td>
                  <td>農業使用免徵地價稅</td>
                </tr>
                <tr>
                  <td><strong>林</strong></td>
                  <td>供種植竹木的林地</td>
                  <td>依林業使用課稅</td>
                </tr>
                <tr>
                  <td><strong>養</strong></td>
                  <td>供水產養殖使用的土地</td>
                  <td>依養殖使用課稅</td>
                </tr>
                <tr>
                  <td><strong>牧</strong></td>
                  <td>供畜牧使用的土地</td>
                  <td>依畜牧使用課稅</td>
                </tr>
                <tr>
                  <td><strong>道</strong></td>
                  <td>供公共通行的道路用地</td>
                  <td>免徵地價稅</td>
                </tr>
                <tr>
                  <td><strong>水</strong></td>
                  <td>供水利、排水、蓄水等使用的土地</td>
                  <td>依使用情形課稅</td>
                </tr>
                <tr>
                  <td><strong>墓</strong></td>
                  <td>供墓地使用的土地</td>
                  <td>依使用情形課稅</td>
                </tr>
              </tbody>
            </table>

            <h3>三、使用分區 vs 地目的關係</h3>
            <p>使用分區與地目是兩個不同的概念，但都會影響土地的使用與價值：</p>

            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="25%">比較項目</th>
                  <th>使用分區</th>
                  <th>地目</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>法源依據</strong></td>
                  <td>都市計畫法、區域計畫法</td>
                  <td>土地法、地籍測量實施規則</td>
                </tr>
                <tr>
                  <td><strong>主要功能</strong></td>
                  <td>管制土地使用方式與開發強度</td>
                  <td>登記土地使用類別，作為稅賦依據</td>
                </tr>
                <tr>
                  <td><strong>變更難易</strong></td>
                  <td>需經都市計畫變更程序，較困難</td>
                  <td>可透過地目變更登記，相對容易</td>
                </tr>
                <tr>
                  <td><strong>重要性</strong></td>
                  <td>決定土地能做什麼用途、能蓋多大</td>
                  <td>影響稅賦計算，登記參考</td>
                </tr>
              </tbody>
            </table>

            <div class="alert alert-warning mt-4">
              <strong>⚠️ 重要觀念</strong>
              <ul class="mb-0">
                <li><strong>使用分區優先於地目</strong> - 土地實際能做什麼用途，以使用分區為準</li>
                <li>例如：某地目為「田」（農地），但使用分區為「住宅區」，則可申請建築使用</li>
                <li>反之，某地目為「建」，但使用分區為「農業區」，則仍受農業使用限制</li>
              </ul>
            </div>

            <h3>四、如何查詢土地使用分區與地目？</h3>

            <h4>查詢方式</h4>
            <ol>
              <li><strong>地政事務所</strong> - 申請土地登記謄本，可查詢地目</li>
              <li><strong>地政雲網站</strong> - 線上查詢地號與地目資訊</li>
              <li><strong>都市計畫書圖</strong> - 各縣市政府都市發展局網站可查詢使用分區</li>
              <li><strong>內政部國土測繪中心</strong> - 提供土地使用分區整合查詢系統</li>
              <li><strong>地政士或建築師</strong> - 委託專業人士協助查詢與解讀</li>
            </ol>

            <h3>五、購地時的注意事項</h3>
            <ul>
              <li>✅ 確認使用分區是否符合購地目的（如建屋需求）</li>
              <li>✅ 了解容積率與建蔽率限制</li>
              <li>✅ 確認是否位於特定管制區（如山坡地、河川區）</li>
              <li>✅ 查詢是否有開發限制（如保護區、古蹟周邊）</li>
              <li>✅ 評估使用分區變更的可行性與成本</li>
              <li>✅ 了解稅賦負擔（地價稅、土地增值稅等）</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">購買土地前，務必查清楚土地使用分區與地目，並評估是否符合購地目的。建議請地政士協助查詢相關資料，避免買到不符需求或無法開發的土地。</p>
            </div>
          `
        },
        'knowledge-5': {
          category: 'wiki',
          date: '2024.09.18',
          title: '土地增值稅如何計算？',
          content: `
            <h2>土地增值稅如何計算？</h2>
            <p>土地增值稅是土地所有權移轉時，就土地自然漲價的部分，由政府課徵的一種稅賦。了解土地增值稅的計算方式與節稅技巧，能有效降低土地交易成本。</p>

            <h3>一、什麼是土地增值稅？</h3>
            <p>根據《土地稅法》規定，土地增值稅是對土地「漲價總數額」所課徵的稅。土地漲價總數額 = 土地移轉現值 - 原規定地價或前次移轉現值 - 改良土地費用。</p>

            <h4>課徵時機</h4>
            <ul>
              <li>土地所有權移轉（買賣、贈與、交換）</li>
              <li>設定典權</li>
              <li>依法徵收土地</li>
              <li>依法照價收買</li>
            </ul>

            <h3>二、土地增值稅稅率</h3>
            <p>土地增值稅採累進稅率，依土地漲價倍數分為三級：</p>

            <h4>一般稅率（累進稅率）</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th>級距</th>
                  <th>土地漲價倍數</th>
                  <th>稅率</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>第一級</td>
                  <td>超過原規定地價或前次申報移轉現值未達100%</td>
                  <td>20%</td>
                </tr>
                <tr>
                  <td>第二級</td>
                  <td>超過原規定地價或前次申報移轉現值100%以上，未達200%</td>
                  <td>30%</td>
                </tr>
                <tr>
                  <td>第三級</td>
                  <td>超過原規定地價或前次申報移轉現值200%以上</td>
                  <td>40%</td>
                </tr>
              </tbody>
            </table>

            <h4>自用住宅優惠稅率（單一稅率）</h4>
            <ul>
              <li><strong>稅率：10%</strong></li>
              <li>大幅降低稅負，僅為一般稅率的一半</li>
              <li>是合法節稅的重要方式</li>
            </ul>

            <h3>三、自用住宅優惠稅率適用條件</h3>
            <p>要享有10%的優惠稅率，必須同時符合以下條件：</p>

            <ol>
              <li><strong>土地所有權人或其配偶、直系親屬在該地辦妥戶籍登記</strong></li>
              <li><strong>無出租或供營業使用</strong></li>
              <li><strong>地上建物為土地所有權人或其配偶、直系親屬所有</strong></li>
              <li><strong>土地面積限制</strong>：
                <ul>
                  <li>都市土地：不超過3公畝（約90.75坪）</li>
                  <li>非都市土地：不超過7公畝（約211.75坪）</li>
                </ul>
              </li>
              <li><strong>一生一次或一生一屋</strong>：
                <ul>
                  <li>一生一次：出售前持有並設籍滿6年</li>
                  <li>一生一屋：年滿65歲，出售前持有並設籍滿6年，且無其他房屋</li>
                </ul>
              </li>
            </ol>

            <div class="alert alert-success mt-3">
              <strong>✅ 自用住宅優惠稅率試算範例</strong>
              <p class="mb-0">假設土地漲價總額為200萬元：</p>
              <ul class="mb-0">
                <li>一般稅率：200萬 × 20% = 40萬元</li>
                <li>自用住宅優惠稅率：200萬 × 10% = 20萬元</li>
                <li><strong>節省：20萬元</strong></li>
              </ul>
            </div>

            <h3>四、土地增值稅計算公式</h3>
            
            <h4>步驟一：計算土地漲價總數額</h4>
            <p><strong>土地漲價總數額 = 申報移轉現值 - 前次移轉現值（或原規定地價）× 台灣地區消費者物價總指數 - 改良土地費用</strong></p>

            <h4>步驟二：依稅率計算應納稅額</h4>
            <ul>
              <li>一般稅率：依20%、30%、40%累進計算</li>
              <li>自用住宅優惠稅率：土地漲價總數額 × 10%</li>
            </ul>

            <h3>五、土地增值稅試算範例</h3>

            <div class="alert alert-light mt-3">
              <strong>📝 案例說明</strong>
              <p>張先生於民國90年購入一塊土地，當時申報移轉現值為500萬元。民國113年出售，申報移轉現值為1,000萬元。假設物價指數調整後前次移轉現值為600萬元，無改良土地費用。</p>
              
              <p class="mt-3"><strong>計算過程：</strong></p>
              <ol>
                <li>土地漲價總數額 = 1,000萬 - 600萬 = 400萬元</li>
                <li>漲價倍數 = 400萬 ÷ 600萬 = 66.67%（未達100%，屬第一級）</li>
                <li>一般稅率計算：400萬 × 20% = <strong>80萬元</strong></li>
                <li>若符合自用住宅：400萬 × 10% = <strong>40萬元</strong></li>
                <li><strong>節稅金額：40萬元</strong></li>
              </ol>
            </div>

            <h3>六、土地增值稅節稅技巧</h3>

            <h4>1. 善用自用住宅優惠稅率</h4>
            <ul>
              <li>出售前確認符合自用住宅條件</li>
              <li>提前6年辦理戶籍登記</li>
              <li>確保土地面積未超過限制</li>
            </ul>

            <h4>2. 重購退稅</h4>
            <ul>
              <li>出售自用住宅用地，2年內重購自用住宅用地</li>
              <li>可申請退還已繳的土地增值稅</li>
              <li>條件：新購土地地價 ≥ 原出售土地地價</li>
            </ul>

            <h4>3. 列舉改良土地費用</h4>
            <ul>
              <li>整地、水土保持等費用可減除</li>
              <li>需保留相關支出憑證</li>
              <li>最多可減除土地漲價總數額的30%</li>
            </ul>

            <h4>4. 夫妻贈與</h4>
            <ul>
              <li>夫妻間贈與土地免徵土地增值稅</li>
              <li>可活用雙方的自用住宅優惠額度</li>
            </ul>

            <h4>5. 分次移轉</h4>
            <ul>
              <li>大面積土地可考慮分次出售</li>
              <li>降低單次漲價倍數，適用較低稅率</li>
            </ul>

            <h3>七、特殊情況與免稅規定</h3>

            <h4>免徵土地增值稅的情況</h4>
            <ul>
              <li>繼承取得之土地（首次移轉）</li>
              <li>私人捐贈土地供公共設施使用</li>
              <li>土地重劃、區段徵收配回之土地</li>
              <li>農地農用移轉給自耕農</li>
              <li>配偶相互贈與土地</li>
            </ul>

            <h3>八、申報繳納期限</h3>
            <ul>
              <li>土地所有權移轉契約簽訂後<strong>30日內</strong>向土地所在地稅捐機關申報</li>
              <li>收到繳納通知後，於<strong>繳納期限內</strong>繳納</li>
              <li>逾期申報：每超過3日加徵1%怠報金，最高加徵至應納稅額</li>
              <li>逾期繳納：每超過2日加徵1%滯納金，最高加徵至15%</li>
            </ul>

            <div class="alert alert-warning mt-4">
              <strong>⚠️ 重要提醒</strong>
              <ul class="mb-0">
                <li>自用住宅優惠稅率條件嚴格，務必提前規劃</li>
                <li>申報時需檢附戶籍謄本、建物所有權狀等文件</li>
                <li>虛報自用住宅將被追繳稅款並處罰</li>
                <li>土地增值稅由賣方負擔，但可由雙方議定負擔方式</li>
              </ul>
            </div>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">土地增值稅是土地交易的重大成本，建議在交易前諮詢地政士或會計師，評估最佳節稅方案。善用自用住宅優惠稅率與重購退稅，可大幅降低稅負。</p>
            </div>
          `
        },
        'knowledge-6': {
          category: 'wiki',
          date: '2024.09.12',
          title: '不動產實價登錄查詢技巧',
          content: `
            <h2>不動產實價登錄查詢技巧</h2>
            <p>實價登錄制度提供真實的不動產交易價格資訊，是買賣雙方議價的重要參考依據。學會正確查詢與分析實價登錄資料，能幫助您做出更明智的購屋決策。</p>

            <h3>一、什麼是實價登錄？</h3>
            <p>實價登錄是依據《平均地權條例》及《不動產經紀業管理條例》規定，要求不動產買賣雙方在交易後，必須向地政機關申報實際交易價格的制度。政府再將這些資訊公開，供民眾查詢。</p>

            <h4>實價登錄的演進</h4>
            <ul>
              <li><strong>2012年8月</strong> - 實價登錄制度上路（1.0版）</li>
              <li><strong>2021年7月</strong> - 實價登錄2.0上路，揭露更詳細資訊</li>
              <li>門牌完整揭露（30戶以下區段化揭露）</li>
              <li>預售屋全面納管，建案資訊更透明</li>
            </ul>

            <h3>二、如何查詢實價登錄？</h3>

            <h4>官方查詢管道</h4>
            <ol>
              <li><strong>內政部不動產交易實價查詢服務網</strong>
                <ul>
                  <li>網址：<code>https://lvr.land.moi.gov.tw/</code></li>
                  <li>提供最完整的官方資料</li>
                  <li>可下載CSV檔案進行分析</li>
                </ul>
              </li>
              <li><strong>地政雲</strong> - 整合地政資訊的便利查詢平台</li>
              <li><strong>各直轄市、縣市地政機關網站</strong> - 提供在地化查詢服務</li>
            </ol>

            <h4>民間查詢平台</h4>
            <ul>
              <li>各大房仲網站（如591、樂屋網、信義房屋等）</li>
              <li>提供更友善的介面與視覺化圖表</li>
              <li>整合周邊生活機能資訊</li>
            </ul>

            <h3>三、查詢步驟教學</h3>

            <h4>步驟 1：選擇查詢方式</h4>
            <ul>
              <li><strong>不動產買賣</strong> - 查詢成屋交易</li>
              <li><strong>預售屋買賣</strong> - 查詢預售屋交易</li>
              <li><strong>不動產租賃</strong> - 查詢租金行情</li>
            </ul>

            <h4>步驟 2：設定查詢條件</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="30%">查詢條件</th>
                  <th>說明</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>時間範圍</strong></td>
                  <td>建議查詢近1年資料，最多可查詢近2年</td>
                </tr>
                <tr>
                  <td><strong>縣市/鄉鎮市區</strong></td>
                  <td>選擇目標區域</td>
                </tr>
                <tr>
                  <td><strong>路名</strong></td>
                  <td>輸入路段名稱，可更精準定位</td>
                </tr>
                <tr>
                  <td><strong>建物型態</strong></td>
                  <td>住宅大樓、華廈、公寓、透天厝等</td>
                </tr>
              </tbody>
            </table>

            <h4>步驟 3：檢視查詢結果</h4>
            <p>查詢結果會顯示以下資訊：</p>
            <ul>
              <li>交易日期</li>
              <li>建物門牌（或區段化範圍）</li>
              <li>交易總價</li>
              <li>單價（每坪價格）</li>
              <li>建物面積（權狀面積、主建物、附屬建物、陽台）</li>
              <li>土地面積</li>
              <li>屋齡</li>
              <li>樓層/總樓層</li>
              <li>車位總價與類型</li>
              <li>交易標的（含車位或不含車位）</li>
            </ul>

            <h3>四、實價登錄資料分析技巧</h3>

            <h4>1. 單價計算要注意</h4>
            <div class="alert alert-warning mt-2">
              <strong>⚠️ 重要提醒</strong>
              <ul class="mb-0">
                <li><strong>權狀坪數 ≠ 室內實際坪數</strong></li>
                <li>權狀包含：主建物 + 附屬建物（陽台） + 公設</li>
                <li>公設比越高，實際室內空間越小</li>
                <li>建議計算「實際使用坪數」的單價</li>
              </ul>
            </div>

            <h4>2. 車位價格分離計算</h4>
            <ul>
              <li>交易總價通常包含車位價格</li>
              <li>應扣除車位價格，單獨計算房屋單價</li>
              <li>車位類型：平面車位、機械車位、坡道車位</li>
            </ul>

            <p class="mt-3"><strong>計算範例：</strong></p>
            <div class="alert alert-light">
              <p>交易總價：1,500萬元（含一個車位）<br>
              車位價格：150萬元<br>
              權狀坪數：40坪<br>
              車位坪數：8坪</p>
              <p class="mt-2"><strong>正確計算：</strong><br>
              房屋價格 = 1,500萬 - 150萬 = 1,350萬<br>
              房屋坪數 = 40坪 - 8坪 = 32坪<br>
              <strong>每坪單價 = 1,350萬 ÷ 32坪 = 42.19萬/坪</strong></p>
            </div>

            <h4>3. 注意特殊交易情況</h4>
            <p>以下情況可能造成價格失真，應排除參考：</p>
            <ul>
              <li>親友間交易（價格可能偏低）</li>
              <li>含裝潢的成交價（總價偏高）</li>
              <li>法拍屋（價格通常較低）</li>
              <li>頂樓或一樓店面（單價與一般樓層差異大）</li>
              <li>特殊格局或瑕疵物件</li>
            </ul>

            <h4>4. 交易時間落差</h4>
            <ul>
              <li>實價登錄有<strong>時間延遲</strong>（約1-2個月）</li>
              <li>查詢的是「過去」的成交價，非當下行情</li>
              <li>房市波動期，應參考最近期資料</li>
            </ul>

            <h4>5. 樣本數量要充足</h4>
            <ul>
              <li>單一筆交易可能有特殊原因，不具代表性</li>
              <li>建議參考至少10-20筆相似物件</li>
              <li>計算平均值、中位數，排除極端值</li>
            </ul>

            <h3>五、進階查詢技巧</h3>

            <h4>1. 比較同社區不同樓層價差</h4>
            <ul>
              <li>輸入完整社區名稱</li>
              <li>比較低樓層、中樓層、高樓層的價差</li>
              <li>評估樓層對價格的影響</li>
            </ul>

            <h4>2. 分析區域價格趨勢</h4>
            <ul>
              <li>查詢同一區域不同時期的成交價</li>
              <li>觀察價格上漲或下跌趨勢</li>
              <li>評估進場時機</li>
            </ul>

            <h4>3. 跨區域比較</h4>
            <ul>
              <li>比較鄰近區域的價格差異</li>
              <li>評估生活機能與價格的合理性</li>
              <li>尋找相對低估的潛力區域</li>
            </ul>

            <h4>4. 預售屋與成屋價差分析</h4>
            <ul>
              <li>比較同區域預售屋與成屋價格</li>
              <li>評估預售屋溢價是否合理</li>
              <li>考量完工時的市場變化風險</li>
            </ul>

            <h3>六、實價登錄的限制與注意事項</h3>

            <h4>實價登錄的限制</h4>
            <ul>
              <li>❌ 30戶以下建物仍採區段化揭露，無法得知確切門牌</li>
              <li>❌ 資料有時間落差，非即時資訊</li>
              <li>❌ 無法得知房屋內部狀況（屋況、裝潢、格局）</li>
              <li>❌ 無法得知交易背景（急售、特殊原因）</li>
              <li>❌ 車位價格包含在總價中，需自行分離計算</li>
            </ul>

            <h4>使用實價登錄時應注意</h4>
            <ul>
              <li>✅ 僅作參考，非絕對標準</li>
              <li>✅ 應搭配實地看屋，了解實際狀況</li>
              <li>✅ 考量個別物件的差異性</li>
              <li>✅ 結合其他資訊（如房仲開價、銀行估價）</li>
              <li>✅ 參考專業人士意見（地政士、房仲）</li>
            </ul>

            <h3>七、議價策略運用</h3>

            <h4>買方策略</h4>
            <ul>
              <li>查詢同社區或鄰近社區的成交價作為議價依據</li>
              <li>了解區域行情，避免買貴</li>
              <li>找出賣方開價與實價登錄的價差</li>
              <li>準備實價登錄資料作為談判籌碼</li>
            </ul>

            <h4>賣方策略</h4>
            <ul>
              <li>參考實價登錄設定合理開價</li>
              <li>了解自身物件的優勢（樓層、格局、屋況）</li>
              <li>比較同社區高價成交案例</li>
              <li>評估市場供需與銷售時機</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">實價登錄是購屋的重要參考工具，但不應是唯一依據。建議結合實地看屋、市場分析、專業諮詢等多方資訊，綜合評估後再做決定。購屋前也可請銀行進行估價，了解貸款成數與市場行情。</p>
            </div>
          `
        },
        'knowledge-7': {
          category: 'wiki',
          date: '2024.09.05',
          title: '農地買賣注意事項',
          content: `
            <h2>農地買賣注意事項</h2>
            <p>農地交易有許多特殊法規限制，與一般建地買賣大不相同。了解農地相關法規、購買資格限制、稅賦優惠與使用限制，避免交易糾紛，確保權益不受損害。</p>

            <h3>一、農地的定義與分類</h3>

            <h4>什麼是農地？</h4>
            <p>農地是指依法供農業使用的土地，包括都市計畫農業區及非都市土地中的農牧用地、林業用地、水利用地等。</p>

            <h4>農地分類</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="25%">分類</th>
                  <th width="35%">定義</th>
                  <th width="40%">特性</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>特定農業區</strong></td>
                  <td>優良農地，嚴格保護</td>
                  <td>原則禁止變更，買賣限制最嚴格</td>
                </tr>
                <tr>
                  <td><strong>一般農業區</strong></td>
                  <td>供農業生產使用</td>
                  <td>有條件允許農舍建築</td>
                </tr>
                <tr>
                  <td><strong>山坡地保育區</strong></td>
                  <td>山坡地農地</td>
                  <td>需考量水土保持，開發受限</td>
                </tr>
                <tr>
                  <td><strong>都市計畫農業區</strong></td>
                  <td>都市計畫內的農地</td>
                  <td>未來可能變更為建地，增值潛力較大</td>
                </tr>
              </tbody>
            </table>

            <h3>二、農地買賣的特殊限制</h3>

            <h4>1. 毗鄰耕地優先購買權</h4>
            <p>《土地法》第104條規定，出售農地時，<strong>毗鄰耕地的所有權人有優先購買權</strong>。</p>
            
            <div class="alert alert-warning mt-3">
              <strong>⚠️ 重要規定</strong>
              <ul class="mb-0">
                <li>賣方必須以書面通知毗鄰地主</li>
                <li>毗鄰地主有<strong>15天</strong>的優先承購期</li>
                <li>若毗鄰地主不買，才能賣給其他人</li>
                <li>若未通知毗鄰地主，毗鄰地主可主張優先購買權，交易可能無效</li>
              </ul>
            </div>

            <h4>2. 農民身分認定</h4>
            <p>2000年《農業發展條例》修正後，取消農地農有（只有農民才能買農地）的限制，<strong>但農地仍須「農用」</strong>。</p>

            <h4>3. 面積限制</h4>
            <ul>
              <li>興建農舍需有農地面積<strong>0.25公頃（約756坪）以上</strong></li>
              <li>各縣市可能有不同規定，需查詢地方法規</li>
            </ul>

            <h3>三、農地可否建築？</h3>

            <h4>農舍建築資格</h4>
            <p>依《農業發展條例》規定，符合以下條件可申請興建農舍：</p>
            <ol>
              <li><strong>具備農民身分</strong>（實際從事農業工作）</li>
              <li><strong>農地面積達0.25公頃以上</strong></li>
              <li><strong>農地需為農業用地</strong>（地目或使用分區符合）</li>
              <li><strong>需在該地設籍滿2年</strong></li>
              <li><strong>本人及配偶、未成年子女名下無自用農舍</strong></li>
            </ol>

            <h4>農舍建築限制</h4>
            <ul>
              <li><strong>建蔽率：10%</strong>（100坪土地，最多蓋10坪）</li>
              <li><strong>容積率：依地方規定</strong>（通常為農舍用地面積×10%）</li>
              <li><strong>最高高度：10.5公尺</strong>（約3層樓）</li>
              <li><strong>農舍用地不得超過農地面積的10%</strong></li>
            </ul>

            <h3>四、農地買賣稅賦優惠</h3>

            <h4>1. 土地增值稅</h4>
            <p><strong>農地農用免徵土地增值稅</strong></p>
            <ul>
              <li>買賣雙方需簽訂「繼續耕作同意書」</li>
              <li>買方需承諾繼續作農業使用</li>
              <li>如未繼續農用，政府將追繳土地增值稅</li>
            </ul>

            <div class="alert alert-success mt-3">
              <strong>✅ 節稅效果顯著</strong>
              <p class="mb-0">範例：農地增值500萬元</p>
              <ul class="mb-0">
                <li>一般土地：500萬 × 20% = 100萬元土地增值稅</li>
                <li>農地農用：<strong>免徵土地增值稅</strong></li>
                <li><strong>節省：100萬元</strong></li>
              </ul>
            </div>

            <h4>2. 地價稅</h4>
            <ul>
              <li><strong>農地農用：免徵地價稅</strong></li>
              <li>如供建築或非農業使用：按一般稅率課徵（10‰-55‰）</li>
            </ul>

            <h4>3. 贈與稅與遺產稅</h4>
            <ul>
              <li><strong>農地農用：免徵遺產稅與贈與稅</strong></li>
              <li>條件：繼承人或受贈人需繼續作農業使用</li>
            </ul>

            <h3>五、農地買賣注意事項</h3>

            <h4>1. 確認農地使用分區與地目</h4>
            <ul>
              <li>查詢土地謄本，確認地目（田、旱、林等）</li>
              <li>查詢都市計畫或非都市土地使用分區</li>
              <li>確認是否可合法作農業使用</li>
            </ul>

            <h4>2. 實地勘查</h4>
            <ul>
              <li>確認土地實際位置與界址</li>
              <li>檢查是否有占用或地上物</li>
              <li>了解水源、道路、排水狀況</li>
              <li>確認是否位於特定管制區（如山坡地、河川區）</li>
            </ul>

            <h4>3. 道路通行權</h4>
            <div class="alert alert-warning mt-2">
              <strong>⚠️ 重要！</strong>
              <p class="mb-0">確認農地是否臨接道路：</p>
              <ul class="mb-0">
                <li>若無道路，需確認是否有合法通行權</li>
                <li>需與鄰地協調通行，避免日後糾紛</li>
                <li>無道路農地價值會大幅降低</li>
              </ul>
            </div>

            <h4>4. 毗鄰耕地查詢</h4>
            <ul>
              <li>查詢地籍圖，確認毗鄰地號</li>
              <li>通知毗鄰地主優先購買權</li>
              <li>保留通知證明，避免交易無效</li>
            </ul>

            <h4>5. 農地貸款較困難</h4>
            <ul>
              <li>農地貸款成數較低（約5-7成）</li>
              <li>利率較高</li>
              <li>需有農民身分或具體農業經營計畫</li>
              <li>建議準備足夠自備款</li>
            </ul>

            <h4>6. 違規使用的風險</h4>
            <div class="alert alert-danger mt-2">
              <strong>🚫 嚴禁違規使用</strong>
              <ul class="mb-0">
                <li>農地不得擅自變更為建地或其他用途</li>
                <li>不得違法興建農舍（資格不符、面積超過）</li>
                <li>違規使用將面臨：追繳稅款、罰款、拆除建物</li>
                <li>若享有免稅優惠卻違規使用，將追繳土地增值稅</li>
              </ul>
            </div>

            <h3>六、農地投資評估</h3>

            <h4>投資優勢</h4>
            <ul>
              <li>✅ 農地農用享有稅賦優惠（免土增稅、地價稅）</li>
              <li>✅ 都市計畫區農地未來可能變更為建地，增值潛力大</li>
              <li>✅ 農地價格相對較低，進入門檻低</li>
              <li>✅ 可作為退休田園生活規劃</li>
            </ul>

            <h4>投資風險</h4>
            <ul>
              <li>❌ 流動性差，較難脫手</li>
              <li>❌ 貸款不易，資金壓力大</li>
              <li>❌ 都市計畫變更不確定性高，時間長</li>
              <li>❌ 違規使用將面臨重罰</li>
              <li>❌ 需實際農用才能享稅賦優惠</li>
            </ul>

            <h3>七、常見農地糾紛</h3>

            <h4>1. 界址糾紛</h4>
            <ul>
              <li>實際使用範圍與地籍圖不符</li>
              <li>解決：買賣前申請土地鑑界</li>
            </ul>

            <h4>2. 優先購買權糾紛</h4>
            <ul>
              <li>未通知毗鄰地主，交易遭撤銷</li>
              <li>解決：買賣前確實通知並保留證明</li>
            </ul>

            <h4>3. 違規使用被罰</h4>
            <ul>
              <li>買到已違規使用的農地，被追繳稅款</li>
              <li>解決：買賣前調查使用現況，要求賣方承諾合法使用</li>
            </ul>

            <h4>4. 道路通行糾紛</h4>
            <ul>
              <li>鄰地不同意通行，影響使用</li>
              <li>解決：買賣前確認通行權，簽訂通行協議</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">農地買賣涉及複雜法規與稅務問題，建議委託專業地政士協助辦理。購買前應確實了解農地使用限制、稅賦優惠條件、建築規定等，並實地勘查確認土地現況。若無農業經營計畫，應審慎評估投資風險。</p>
            </div>
          `
        },
        'knowledge-8': {
          category: 'wiki',
          date: '2024.08.28',
          title: '地上權與所有權的差別',
          content: `
            <h2>地上權與所有權的差別</h2>
            <p>地上權住宅近年在市場上越來越常見，以相對低廉的價格吸引購屋者目光。然而，地上權與一般所有權有本質上的差異，購買前務必了解其權利內容、使用年限、貸款限制與轉售風險，避免買了才後悔。</p>

            <h3>一、什麼是地上權？</h3>

            <h4>地上權的定義</h4>
            <p>地上權是《民法》規定的一種「用益物權」，指在他人土地上有建築物或其他工作物，並使用該土地的權利。<strong>簡單來說，就是「有房沒地」</strong>。</p>

            <h4>地上權 vs 所有權</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="25%">項目</th>
                  <th>所有權</th>
                  <th>地上權</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>土地所有權</strong></td>
                  <td>✅ 擁有土地所有權</td>
                  <td>❌ 僅有使用權，無所有權</td>
                </tr>
                <tr>
                  <td><strong>持有年限</strong></td>
                  <td>✅ 永久持有</td>
                  <td>⏳ 有使用期限（通常50-70年）</td>
                </tr>
                <tr>
                  <td><strong>房屋所有權</strong></td>
                  <td>✅ 永久擁有</td>
                  <td>⚠️ 到期後需拆除或續約</td>
                </tr>
                <tr>
                  <td><strong>地租</strong></td>
                  <td>❌ 不需繳納</td>
                  <td>✅ 需定期繳納地租給地主</td>
                </tr>
                <tr>
                  <td><strong>價格</strong></td>
                  <td>💰 較高</td>
                  <td>💰 約為所有權的5-7折</td>
                </tr>
              </tbody>
            </table>

            <h3>二、地上權的類型</h3>

            <h4>1. 普通地上權</h4>
            <ul>
              <li>可自由轉讓、繼承、設定抵押</li>
              <li>權利較完整</li>
              <li>市場上較常見</li>
            </ul>

            <h4>2. 區分地上權</h4>
            <ul>
              <li>將土地分為地上、地面、地下不同層次使用</li>
              <li>例如：捷運共構住宅（住宅在地上，捷運在地下）</li>
              <li>使用空間較受限</li>
            </ul>

            <h3>三、地上權的優缺點分析</h3>

            <h4>地上權的優點</h4>
            <ul>
              <li>✅ <strong>價格較低</strong> - 約為所有權住宅的5-7折，總價門檻低</li>
              <li>✅ <strong>免土地增值稅</strong> - 轉讓時無土地增值稅負擔</li>
              <li>✅ <strong>地價稅較低</strong> - 無土地所有權，不需繳納高額地價稅</li>
              <li>✅ <strong>精華地段可負擔</strong> - 能以較低價格住進蛋黃區</li>
            </ul>

            <h4>地上權的缺點</h4>
            <ul>
              <li>❌ <strong>有使用年限</strong> - 到期後房屋需拆除或續約（不一定能續）</li>
              <li>❌ <strong>需繳地租</strong> - 每年需支付土地租金，且可能調漲</li>
              <li>❌ <strong>貸款困難</strong> - 成數低（約5-6成）、利率高</li>
              <li>❌ <strong>轉售不易</strong> - 願意接手的買家較少，流動性差</li>
              <li>❌ <strong>增值空間有限</strong> - 隨年限遞減，越接近到期越不值錢</li>
              <li>❌ <strong>無法參與都更</strong> - 沒有土地所有權，無法主導都更</li>
            </ul>

            <h3>四、地上權住宅的費用</h3>

            <h4>1. 地租計算</h4>
            <p><strong>地租 = 土地申報地價 × 地租率（通常3.5%-5%）</strong></p>

            <div class="alert alert-light mt-3">
              <strong>📝 地租試算範例</strong>
              <p>假設：</p>
              <ul>
                <li>土地申報地價：1,000萬元</li>
                <li>地租率：5%</li>
                <li>權狀坪數：30坪，土地持分比例1%</li>
              </ul>
              <p class="mt-2"><strong>計算：</strong></p>
              <p>年度地租 = 1,000萬 × 1% × 5% = <strong>5,000元/年</strong></p>
              <p class="text-muted mb-0">註：實際地租會隨申報地價調整而變動</p>
            </div>

            <h4>2. 地租調整機制</h4>
            <ul>
              <li>地租通常<strong>每3-5年調整一次</strong></li>
              <li>依土地公告地價調整</li>
              <li>房價上漲區域，地租也可能上漲</li>
            </ul>

            <h4>3. 其他費用</h4>
            <ul>
              <li>管理費（與所有權住宅相同）</li>
              <li>房屋稅（僅就建物課徵，較低）</li>
              <li>水電瓦斯等生活費用</li>
            </ul>

            <h3>五、地上權住宅貸款問題</h3>

            <h4>貸款條件比較</h4>
            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="25%">項目</th>
                  <th>所有權住宅</th>
                  <th>地上權住宅</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>貸款成數</strong></td>
                  <td>7-8成</td>
                  <td>5-6成（甚至更低）</td>
                </tr>
                <tr>
                  <td><strong>貸款利率</strong></td>
                  <td>2%-2.5%</td>
                  <td>2.5%-3.5%（較高）</td>
                </tr>
                <tr>
                  <td><strong>貸款年限</strong></td>
                  <td>最長30-40年</td>
                  <td>不得超過地上權剩餘年限</td>
                </tr>
                <tr>
                  <td><strong>承貸銀行</strong></td>
                  <td>多數銀行接受</td>
                  <td>願意承貸的銀行較少</td>
                </tr>
              </tbody>
            </table>

            <div class="alert alert-warning mt-3">
              <strong>⚠️ 自備款壓力大</strong>
              <p class="mb-0">地上權住宅貸款成數低，購屋者需準備較高的自備款：</p>
              <ul class="mb-0">
                <li>總價1,000萬的地上權住宅</li>
                <li>貸款成數僅5成 = 貸款500萬</li>
                <li><strong>需自備款：500萬元</strong>（所有權住宅約需200-300萬）</li>
              </ul>
            </div>

            <h3>六、地上權到期後怎麼辦？</h3>

            <h4>處理方式</h4>
            <ol>
              <li><strong>續約</strong>
                <ul>
                  <li>需與地主（通常是政府）協商</li>
                  <li>不一定能續約成功</li>
                  <li>續約條件可能不利（地租大漲）</li>
                </ul>
              </li>
              <li><strong>地主收回</strong>
                <ul>
                  <li>地主可收回土地</li>
                  <li>需補償地上建物價值</li>
                  <li>補償金額通常遠低於當初購買價</li>
                </ul>
              </li>
              <li><strong>拆除</strong>
                <ul>
                  <li>無法續約時，需自行拆除建物</li>
                  <li>拆除費用由住戶負擔</li>
                  <li>等於房子價值歸零</li>
                </ul>
              </li>
            </ol>

            <h3>七、誰適合購買地上權住宅？</h3>

            <h4>適合族群</h4>
            <ul>
              <li>✅ 預算有限，想住精華地段</li>
              <li>✅ 年紀較大，僅需使用20-30年</li>
              <li>✅ 不需考慮轉售</li>
              <li>✅ 自備款充足（貸款成數低）</li>
              <li>✅ 能承擔未來地租調漲風險</li>
            </ul>

            <h4>不適合族群</h4>
            <ul>
              <li>❌ 希望長期持有並傳承子女</li>
              <li>❌ 預期房產增值獲利</li>
              <li>❌ 自備款不足，需高成數貸款</li>
              <li>❌ 擔心未來轉售問題</li>
              <li>❌ 無法承擔地租調漲風險</li>
            </ul>

            <h3>八、購買地上權住宅注意事項</h3>

            <h4>1. 確認剩餘年限</h4>
            <ul>
              <li>查詢地上權設定日期與到期日</li>
              <li>剩餘年限越短，價值越低</li>
              <li>建議至少剩餘40年以上</li>
            </ul>

            <h4>2. 了解地租條件</h4>
            <ul>
              <li>目前地租金額與計算方式</li>
              <li>地租調整機制與頻率</li>
              <li>歷史調漲幅度</li>
            </ul>

            <h4>3. 確認到期處理方式</h4>
            <ul>
              <li>是否可續約？續約條件？</li>
              <li>無法續約時的補償機制</li>
              <li>地主是誰？（政府或私人）</li>
            </ul>

            <h4>4. 評估貸款條件</h4>
            <ul>
              <li>詢問多家銀行貸款意願</li>
              <li>確認貸款成數、利率、年限</li>
              <li>評估自備款是否足夠</li>
            </ul>

            <h4>5. 考慮轉售風險</h4>
            <ul>
              <li>地上權住宅流動性差</li>
              <li>轉售時價格可能大幅折損</li>
              <li>購買前做好長期持有準備</li>
            </ul>

            <div class="alert alert-danger mt-4">
              <strong>🚫 常見迷思</strong>
              <ul class="mb-0">
                <li>❌ 「地上權比較便宜，很划算」→ 要考慮年限遞減、轉售困難</li>
                <li>❌ 「到期政府一定會續約」→ 不一定，需視政策而定</li>
                <li>❌ 「省下來的錢可以投資」→ 需評估投資報酬是否能彌補房產損失</li>
                <li>❌ 「反正幾十年後我也用不到」→ 要考慮子女繼承與轉售問題</li>
              </ul>
            </div>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">地上權住宅價格雖然較低，但背後有諸多限制與風險。購買前應充分了解使用年限、地租負擔、貸款條件、轉售難度等問題，並評估自身需求與財務狀況。建議諮詢地政士或不動產專業人士，審慎評估後再決定。若非必要，一般仍建議優先考慮完整所有權的房產。</p>
            </div>
          `
        },
        'knowledge-9': {
          category: 'wiki',
          date: '2024.08.20',
          title: '重劃與徵收的區別',
          content: `
            <h2>重劃與徵收的區別</h2>
            <p>土地重劃與區段徵收是政府進行土地開發與都市計畫的兩種主要手段，雖然目的相似，但在法源依據、執行方式、土地所有權變動、補償方式等方面有顯著差異。了解兩者差異，有助於地主維護自身權益。</p>

            <h3>一、基本概念</h3>

            <h4>什麼是土地重劃？</h4>
            <p>土地重劃是指將一定區域內畸零細碎的土地，重新規劃整理，並依照原有土地面積比例，<strong>分配位置較好、面積較小的土地</strong>給原地主。地主<strong>保有土地所有權</strong>，只是位置與面積改變。</p>

            <h4>什麼是區段徵收？</h4>
            <p>區段徵收是指政府為了開發建設需要，<strong>將土地全部徵收為公有</strong>，經過重新規劃整理後，部分土地供公共設施使用，其餘土地讓原地主<strong>選擇領回抵價地或領取現金補償</strong>。</p>

            <h3>二、重劃與徵收的比較</h3>

            <table class="table table-bordered mt-3">
              <thead>
                <tr>
                  <th width="20%">比較項目</th>
                  <th width="40%">市地重劃</th>
                  <th width="40%">區段徵收</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>法源依據</strong></td>
                  <td>《平均地權條例》<br>《獎勵土地所有權人辦理市地重劃辦法》</td>
                  <td>《土地徵收條例》</td>
                </tr>
                <tr>
                  <td><strong>土地所有權</strong></td>
                  <td>✅ 地主<strong>保有所有權</strong></td>
                  <td>❌ 土地所有權<strong>移轉給政府</strong></td>
                </tr>
                <tr>
                  <td><strong>辦理主體</strong></td>
                  <td>• 政府辦理（公辦重劃）<br>• 地主自辦（自辦重劃）</td>
                  <td>政府辦理</td>
                </tr>
                <tr>
                  <td><strong>地主同意門檻</strong></td>
                  <td>自辦重劃需半數以上地主同意<br>公辦重劃無需同意</td>
                  <td>無需地主同意，政府依法執行</td>
                </tr>
                <tr>
                  <td><strong>補償方式</strong></td>
                  <td>分配整理後的土地（面積減少但價值提升）</td>
                  <td>• 領取現金補償<br>• 或選擇領回抵價地</td>
                </tr>
                <tr>
                  <td><strong>負擔方式</strong></td>
                  <td>• 工程費用負擔<br>• 公共設施用地負擔<br>• 重劃費用負擔<br>（總計約40%-45%土地面積）</td>
                  <td>• 公共設施用地負擔<br>• 抵價地負擔（總計約40%-50%土地面積）</td>
                </tr>
                <tr>
                  <td><strong>土地增值稅</strong></td>
                  <td>減半徵收</td>
                  <td>免徵（首次移轉）</td>
                </tr>
              </tbody>
            </table>

            <h3>三、市地重劃詳細說明</h3>

            <h4>重劃的類型</h4>
            <ol>
              <li><strong>公辦重劃</strong>
                <ul>
                  <li>由政府主導辦理</li>
                  <li>不需地主同意，政府依法執行</li>
                  <li>政府承擔財務風險</li>
                  <li>較常見</li>
                </ul>
              </li>
              <li><strong>自辦重劃</strong>
                <ul>
                  <li>由地主自行發起</li>
                  <li>需土地所有權人過半數同意，且其所有土地面積超過重劃區總面積1/2</li>
                  <li>地主成立「重劃會」辦理</li>
                  <li>地主主導權較高</li>
                </ul>
              </li>
            </ol>

            <h4>重劃流程</h4>
            <ol>
              <li>劃定重劃區</li>
              <li>測量與地籍整理</li>
              <li>擬定重劃計畫書</li>
              <li>公告與異議處理</li>
              <li>土地分配設計</li>
              <li>工程施工（道路、排水、公園等）</li>
              <li>土地分配與登記</li>
              <li>完工驗收</li>
            </ol>

            <h4>重劃負擔（抵費地）</h4>
            <p>地主需負擔的土地面積（合計約40%-45%）：</p>
            <ul>
              <li><strong>公共設施用地負擔</strong>（約25%-30%）- 道路、公園、綠地等</li>
              <li><strong>重劃費用負擔</strong>（約10%-15%）- 工程費、地價查估費、整地費等</li>
            </ul>

            <div class="alert alert-light mt-3">
              <strong>📝 重劃分配範例</strong>
              <p>張先生原有土地：1,000平方公尺</p>
              <ul>
                <li>公共設施負擔：300平方公尺（30%）</li>
                <li>重劃費用負擔：150平方公尺（15%）</li>
                <li><strong>分配回來土地：550平方公尺（55%）</strong></li>
              </ul>
              <p class="mt-2">雖然面積減少45%，但因為：</p>
              <ul class="mb-0">
                <li>✅ 土地形狀方正，不再畸零</li>
                <li>✅ 臨接道路，交通便利</li>
                <li>✅ 公共設施完善</li>
                <li>✅ <strong>土地單價大幅提升，總價值可能增加</strong></li>
              </ul>
            </div>

            <h4>重劃的優缺點</h4>
            <p><strong>優點：</strong></p>
            <ul>
              <li>✅ 保有土地所有權</li>
              <li>✅ 土地價值提升</li>
              <li>✅ 公共設施完善</li>
              <li>✅ 土地增值稅減半</li>
            </ul>

            <p><strong>缺點：</strong></p>
            <ul>
              <li>❌ 土地面積減少（約45%）</li>
              <li>❌ 重劃期間無法使用土地</li>
              <li>❌ 分配位置可能改變</li>
            </ul>

            <h3>四、區段徵收詳細說明</h3>

            <h4>區段徵收的目的</h4>
            <ul>
              <li>新都市開發（如新市鎮、重劃區）</li>
              <li>舊都市更新</li>
              <li>農村社區更新</li>
              <li>重大建設（如機場、高鐵特定區）</li>
            </ul>

            <h4>區段徵收流程</h4>
            <ol>
              <li>發布徵收計畫</li>
              <li>地籍調查及地價評估</li>
              <li>公告徵收（地主可提出異議）</li>
              <li>發放補償費或配地通知</li>
              <li>土地開發整理（政府興建公共設施）</li>
              <li>配地及登記</li>
            </ol>

            <h4>地主的選擇</h4>
            <p>地主可選擇以下兩種方式之一：</p>

            <ol>
              <li><strong>領取現金補償</strong>
                <ul>
                  <li>依徵收時的公告現值加成補償（通常為公告現值的120%）</li>
                  <li>一次領取，不再保有土地</li>
                  <li>適合不想持有土地、急需現金者</li>
                </ul>
              </li>
              <li><strong>領回抵價地</strong>
                <ul>
                  <li>選擇領回整理後的土地</li>
                  <li>依照原土地價值比例，分配整理後的土地</li>
                  <li>土地價值提升，但面積減少</li>
                  <li>適合看好未來發展、願意長期持有者</li>
                </ul>
              </li>
            </ol>

            <div class="alert alert-light mt-3">
              <strong>📝 區段徵收範例</strong>
              <p>李先生土地被區段徵收，原地價1,000萬元</p>
              
              <p class="mt-3"><strong>選擇一：領取現金補償</strong></p>
              <ul>
                <li>補償金額：1,000萬 × 120% = <strong>1,200萬元</strong></li>
                <li>不再保有土地</li>
              </ul>
              
              <p class="mt-3"><strong>選擇二：領回抵價地</strong></p>
              <ul>
                <li>假設開發後土地單價增值50%</li>
                <li>可領回抵價地價值：1,000萬元（等值）</li>
                <li>但因單價提升50%，實際領回面積約為原來的40%</li>
                <li>未來增值空間大</li>
              </ul>
            </div>

            <h4>區段徵收的優缺點</h4>
            <p><strong>優點：</strong></p>
            <ul>
              <li>✅ 可選擇領現金或領地</li>
              <li>✅ 抵價地土地價值提升</li>
              <li>✅ 土地增值稅免徵（首次移轉）</li>
              <li>✅ 現金補償有加成（120%）</li>
            </ul>

            <p><strong>缺點：</strong></p>
            <ul>
              <li>❌ 土地所有權被徵收</li>
              <li>❌ 領回抵價地面積大幅減少（約40%-50%）</li>
              <li>❌ 現金補償可能低於市價</li>
              <li>❌ 開發期間長（通常5-10年）</li>
            </ul>

            <h3>五、重劃與徵收的選擇</h3>

            <h4>什麼情況適合重劃？</h4>
            <ul>
              <li>土地所有權分散，需整合</li>
              <li>土地畸零細碎，開發不易</li>
              <li>公共設施不足，需重新規劃</li>
              <li>地主希望保有土地所有權</li>
            </ul>

            <h4>什麼情況適合區段徵收？</h4>
            <ul>
              <li>新市鎮或重大建設開發</li>
              <li>需大規模土地變更</li>
              <li>政府主導開發案</li>
              <li>地主意願整合困難</li>
            </ul>

            <h3>六、地主權益保障</h3>

            <h4>重劃時的權益</h4>
            <ul>
              <li>✅ 有權查閱重劃計畫書</li>
              <li>✅ 可對分配結果提出異議</li>
              <li>✅ 可請求交換分配位置</li>
              <li>✅ 土地增值稅減半優惠</li>
            </ul>

            <h4>區段徵收時的權益</h4>
            <ul>
              <li>✅ 可選擇領現金或領回抵價地</li>
              <li>✅ 可對徵收補償金額提出異議</li>
              <li>✅ 可申請優先選配抵價地位置</li>
              <li>✅ 土地增值稅免徵（首次移轉）</li>
            </ul>

            <h3>七、常見問題</h3>

            <h4>Q1: 重劃或徵收一定要參加嗎？</h4>
            <p>A: 公辦重劃與區段徵收是政府依法執行，<strong>地主無法拒絕</strong>。但自辦重劃需地主同意才能辦理。</p>

            <h4>Q2: 土地面積減少，值得嗎？</h4>
            <p>A: 雖然面積減少，但因為土地品質提升（方正、臨路、公設完善），<strong>單價通常大幅提升，總價值可能增加</strong>。需視個案評估。</p>

            <h4>Q3: 重劃或徵收期間，土地可以買賣嗎？</h4>
            <p>A: 可以，但<strong>買方將承擔重劃或徵收的權利義務</strong>。交易時價格會考量重劃或徵收因素。</p>

            <h4>Q4: 如何知道土地是否被劃入重劃或徵收範圍？</h4>
            <p>A: 查詢方式：</p>
            <ul>
              <li>各縣市政府地政局網站</li>
              <li>內政部不動產資訊平台</li>
              <li>向地政事務所查詢</li>
              <li>委託地政士協助查詢</li>
            </ul>

            <div class="alert alert-warning mt-4">
              <strong>⚠️ 重要提醒</strong>
              <ul class="mb-0">
                <li>重劃與徵收對土地價值影響重大，應密切注意相關公告</li>
                <li>收到通知後，應詳細了解權益與負擔</li>
                <li>可向政府機關提出疑問與異議</li>
                <li>重大決策（如領現金或領地）應審慎評估</li>
              </ul>
            </div>

            <div class="alert alert-info mt-4">
              <strong>💡 專業建議</strong>
              <p class="mb-0">重劃與區段徵收涉及複雜的土地權益變動，建議委託專業地政士或律師協助處理。應仔細閱讀相關文件，了解權利義務，評估各種選擇的利弊，必要時可提出異議或參加說明會，維護自身權益。</p>
            </div>
          `
        },
        // 新聞總覽
        'news-10': {
          category: 'news',
          date: '2025.11.18',
          title: '桃園航空城優先產業專用區 I 基地開發計畫 - 台壽、榮運攜手打造國際級巨蛋與產業城',
          content: `
            <h2>🔥桃園航空城優先產業專用區 I 基地開發計畫</h2>
            <h3>台壽、榮運攜手打造國際級巨蛋與產業城 🇹🇼</h3>
            
            <p class="text-muted"><small>來源：長發地產 | 2025-11-18</small></p>

            <p>在今年的重大建設消息中，有一則訊息至今仍然讓人覺得——桃園真的正在創造自己的冠軍時代！那就是：<strong>桃園航空城優先產業專用區 I 基地的大開發計畫</strong>，牽動產業 × 商業 × 城市升級。</p>

            <p>更值得一提的是，這塊基地不只是由台灣人壽保險股份有限公司單獨運作，還與榮運股份有限公司（簡稱「榮運」）合作，雙方共同成立合資公司<strong>中信榮盛股份有限公司</strong>，攜手開發此案。</p>

            <h3>🏟️ 國際級巨蛋場館｜桃園的未來主場</h3>
            
            <p>由<strong>陳廷杰建築師事務所</strong>負責規劃設計，未來將興建能容納近萬名觀眾的<strong>國際運動賽事級巨蛋 ＋ 大型演藝空間</strong>。</p>

            <p>而基地主體開發將由台灣人壽 • 榮運合資公司攜手推動，整合倉儲物流、旅宿、商場、運動場館，形成真正的「城市中心」級複合用途。</p>

            <div class="alert alert-success mt-3">
              <p><strong>想像那個畫面：</strong></p>
              <ul class="mb-0">
                <li>⚾ 國際賽滿場吶喊</li>
                <li>🎤 上萬人演唱會震撼夜空</li>
                <li>🏆 桃園成為北台灣新娛樂與產業核心</li>
              </ul>
            </div>

            <p class="mt-3">這不是只是蓋一座建築，是把<strong>台灣的舞台、桃園的自信</strong>放到世界地圖上！</p>

            <h3>🏙️ 更精彩的是——一整座「未來產業城」同步展開</h3>

            <p>基地面積達約 <strong>18.9 公頃（約 189,391.13 ㎡）</strong>💥💥</p>

            <p>台灣人壽得標後，總投資規模預估<strong>超過 500 億元</strong>。</p>

            <p>在榮運合作加入後，透過合資公司將資源與專長結合，打造「倉儲 + 商場 + 旅宿 + 運動場館」一體化園區。</p>

            <div class="alert alert-info mt-3">
              <p><strong>這裡，將是：</strong></p>
              <ul class="mb-0">
                <li>📈 新商機的起點</li>
                <li>🌏 國際企業的登台入口</li>
                <li>💼 桃園下一個黃金成長引擎</li>
              </ul>
            </div>

            <h3>📌 建案資訊｜桃園航空城優先產專區 I 基地</h3>

            <table class="table table-bordered mt-3">
              <tbody>
                <tr>
                  <td width="30%"><strong>位置</strong></td>
                  <td>桃園市</td>
                </tr>
                <tr>
                  <td><strong>業主</strong></td>
                  <td>台灣人壽 × 榮運（合資公司：中信榮盛）</td>
                </tr>
                <tr>
                  <td><strong>土地面積</strong></td>
                  <td>189,391.13 ㎡（約 18.9 公頃）</td>
                </tr>
                <tr>
                  <td><strong>總樓地板面積</strong></td>
                  <td>132,584 ㎡（原規劃）</td>
                </tr>
                <tr>
                  <td><strong>樓層規劃</strong></td>
                  <td>B1–5F</td>
                </tr>
                <tr>
                  <td><strong>類型</strong></td>
                  <td>商辦／綜合用途／巨蛋＋旅宿＋產業</td>
                </tr>
                <tr>
                  <td><strong>年份</strong></td>
                  <td>2025（啟動營運）</td>
                </tr>
                <tr>
                  <td><strong>風格</strong></td>
                  <td>現代</td>
                </tr>
                <tr>
                  <td><strong>資訊來源</strong></td>
                  <td>陳廷杰建築師事務所</td>
                </tr>
              </tbody>
            </table>

            <h3>🎉 長發觀點｜這不是一般利多，是值得被重播的「冠軍級利多」！</h3>

            <p>即使不是今天的消息，但它帶來的外溢效應仍在持續發酵：</p>

            <p><strong>都因航空城的大動作，而迎來更扎實的城市升級。</strong></p>

            <p>而且，有台灣人壽與榮運兩大實力企業參與，專案規模與資金實力更被市場高度注目。</p>

            <p class="lead mt-4"><strong>這就是桃園下一個黃金十年的主旋律！</strong></p>

            <div class="alert alert-warning mt-4">
              <strong>✨ 長發地產｜投資看趨勢，趨勢找長發‼️</strong>
              <p class="mb-0 mt-2">我們會持續整理最關鍵的資訊，帶大家看懂桃園的下一步。</p>
            </div>
          `
        },
        'news-11': {
          category: 'news',
          date: '2025.11.17',
          title: '經國特區好消息！幸福路跨南崁溪「人行跨橋」正式啟動',
          content: `
            <h2>🚶‍♂️🌉 經國特區好消息！幸福路跨南崁溪「人行跨橋」正式啟動</h2>
            
            <p class="text-muted"><small>來源：長發地產 | 2025-11-17</small></p>

            <p class="lead">桃園城市升級，再添一筆重大利多！</p>

            <p>桃園市政府水務局正式公告：</p>

            <p class="lead"><strong>➡️ 「北溪溪幸福路人行跨橋」工程說明會開跑！</strong></p>

            <p>這代表本案已正式從規劃走向實質推動階段，是經國特區跨溪連結的重要里程碑。</p>

            <div class="alert alert-info mt-3">
              <h4>📅 工程說明會資訊</h4>
              <ul class="mb-0">
                <li><strong>時間：</strong>11/17 晚上 7 點</li>
                <li><strong>地點：</strong>沅洲里民活動中心｜峭眉二街26號 2 樓</li>
              </ul>
            </div>

            <h3>🌟 這座跨橋，會為經國特區帶來什麼？</h3>

            <h4>✅ 1. 跨溪生活圈正式串連</h4>
            <p>幸福路從經國特區延伸至南崁溪另一側，居民步行、孩子上下學、長輩散步都更安全便捷。</p>

            <h4>✅ 2. 經國特區可近性再升級</h4>
            <p>結合 G12 捷運站、經國轉運站、南崁溪綠帶，未來整個生活圈將更完整、更具吸引力。</p>

            <h4>✅ 3. 重大建設一項項開出來，生活機能加速成形</h4>
            <p>這不是紙上規劃，而是官方正式啟動的工程，區域價值自然持續加溫。</p>

            <div class="alert alert-success mt-4">
              <h4>💡 經國特區本來就強，再加上跨橋工程，地段優勢更明確！</h4>
              
              <p class="mt-3"><strong>經國特區靠近：</strong></p>
              <ul class="mb-0">
                <li>✔ 南崁交流道</li>
                <li>✔ 經國轉運站</li>
                <li>✔ 未來綠線 G12 站</li>
                <li>✔ 南崁溪水岸生活圈</li>
                <li>✔ 機場與老城區連接</li>
              </ul>
            </div>

            <p class="mt-4">再＋上這次跨橋工程，等於把整個生活圈連成一張<strong>「宜居大網」</strong>，通行更順、環境更好、人口更穩，對自住、企業主、長期投資者都是大加分。</p>

            <h3>🏢✨ 長發地產就在經國特區核心位置！</h3>

            <p class="lead"><strong>地段決定價值，趨勢決定眼光。</strong></p>

            <p>長發地產選址在經國特區，就是看準這裡是<strong>桃園未來最具爆發力的黃金板塊</strong>！</p>

            <p>我們深耕桃園多年，看趨勢、抓節奏：</p>

            <ul>
              <li>📈 建設往哪裡走，價值就往哪裡走</li>
              <li>📈 趨勢越早看見，越能把握真正的漲幅</li>
            </ul>

            <div class="alert alert-warning mt-4">
              <h4>👉 投資看趨勢，趨勢找長發‼️</h4>
              <p class="mb-0">👉 長發陪你掌握桃園每一次的城市升級。</p>
            </div>

            <div class="alert alert-light mt-4">
              <h5>🏢 長發地產｜經國特區據點</h5>
              <p class="mb-0">
                <strong>地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>電話：</strong>03-3568268
              </p>
            </div>
          `
        },
        'news-13': {
          category: 'news',
          date: '2025.11.13',
          title: '桃園重大產業用地標售｜桃科二期 46,682 坪正式釋出',
          content: `
            <h2>🚀 桃園重大產業用地標售｜桃科二期 46,682 坪正式釋出</h2>
            
            <p class="text-muted"><small>來源：長發地產 | 2025-11-13</small></p>

            <div class="alert alert-warning mt-4">
              <h3 class="text-center">北台灣科技廊帶真正的「最後一塊拼圖」，正式登場！</h3>
            </div>

            <p class="lead">桃園科技工業園區第二期（桃科二期）首波釋出 <strong>46,682 坪</strong>，不僅是北台灣最新的工業區土地，更具備完整產權、公共設施完備、AI＋製造＋物流產業皆高度關注。</p>

            <h3 class="mt-5">🔑 三大主軸完整解析</h3>

            <h3>➊ 桃科地點優勢｜北台灣科技走廊核心位置</h3>

            <p><strong>桃園＝全台工業產值連十年第一，北台灣產業鏈的真正心臟。</strong></p>

            <div class="alert alert-info mt-3">
              <h4>桃科二期位於：</h4>
              <ul class="mb-0">
                <li>📍 <strong>桃科一期旁＋緊鄰觀音工業區</strong></li>
                <li>📍 串聯「<strong>竹科 ➝ 桃園 ➝ 台北港／內科／南港</strong>」科技走廊</li>
                <li>📍 靠近<strong>桃園航空城、桃園國際機場</strong></li>
                <li>📍 擁有北台灣最完整的<strong>研發－製造－物流供應鏈</strong></li>
              </ul>
            </div>

            <p class="mt-3">周邊科技能量密度極高，是企業布局 <strong>AI 伺服器、資通訊、智慧製造</strong>的絕佳場址。</p>
            <p>在產業用地「一地難求」的情況下，本案地點可說是<strong class="text-danger">含金量最高的出手窗口</strong>。</p>

            <h3 class="mt-5">➋ 標售付款方式（本案最大亮點）</h3>

            <div class="alert alert-success">
              <h3 class="text-center">✨ 先租後售＝最友善企業的資金模式 ✨</h3>
            </div>

            <h4>💰 只需 15% 即可提前卡位</h4>
            <p>廠商只要繳 <strong>得標價 15% 的租金＋保證金</strong>，即可立即進駐規劃、準備設廠，大幅降低前期現金壓力。</p>

            <h4>🕒 1.5 年建照時間＝企業資金調度最佳緩衝期</h4>
            <p>企業能在這段期間完成：</p>
            <ul>
              <li>✔ 建廠設計</li>
              <li>✔ 產線規劃</li>
              <li>✔ 資金調度</li>
              <li>✔ 機台導入計畫</li>
            </ul>
            <p class="lead">讓建廠節奏不必急促，財務更穩健。</p>

            <h4>💵 分期付款市府招商讓利，貼近企業現金流</h4>
            <div class="alert alert-light mt-3">
              <p class="mb-0">
                <strong>二款：</strong>10%<br>
                <strong>尾款：</strong>75%（完成建照後、土地移轉前支付）
              </p>
            </div>

            <p class="mt-3">等於<strong>「先建後買」</strong>，政府協助企業降低資金壓力、順利落地生產，真正達成 <strong class="text-success">市府 × 產業雙贏</strong>。</p>

            <h3 class="mt-5">➌ 招標時程｜把握卡位時機</h3>

            <div class="alert alert-warning mt-3">
              <ul class="mb-0">
                <li>🗓 <strong>正式公告｜</strong>114 年 11 月 13 日（四）</li>
                <li>🗓 <strong>投標截止｜</strong>115 年 1 月 13 日（二）上午 9:00</li>
              </ul>
            </div>

            <p class="mt-4">標售資訊可至：</p>
            <p>🔗 <strong>桃園市政府地政局 → 土地標售專區</strong></p>
            <p><a href="https://land.tycg.gov.tw/" target="_blank" rel="noopener noreferrer" class="btn btn-primary">👉 前往桃園市地政局官網</a></p>

            <div class="alert alert-danger mt-5">
              <h3 class="text-center">🎯 長發地產｜專業產業用地諮詢</h3>
              <p class="text-center mb-0">
                我們提供企業主與投資人<strong>最即時、最專業的土地標售資訊與策略建議</strong>，<br>
                協助您在北台灣科技走廊上，找到最適合的產業落點。
              </p>
            </div>

            <div class="alert alert-light mt-4">
              <h5>🏢 長發地產｜聯絡資訊</h5>
              <p class="mb-0">
                <strong>地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>電話：</strong>03-3568268<br>
                <strong>服務項目：</strong>產業用地開發、土地標售諮詢、企業選址規劃
              </p>
            </div>

            <p class="mt-4 text-center lead"><strong>💼 桃園產業升級，從找對土地開始｜長發地產陪您前行</strong></p>
          `
        },
        'news-14': {
          category: 'news',
          date: '2025.11.06',
          title: '桃園建設大紀事｜國道2號「大竹交流道改善工程」正式動土',
          content: `
            <h2>🚧 桃園建設大紀事｜國道2號「大竹交流道改善工程」正式動土 🚀</h2>
            
            <p class="text-muted"><small>來源：長發地產 | 2025-11-06</small></p>

            <div class="alert alert-success mt-4">
              <h3 class="text-center">桃園交通建設再添重磅一筆！</h3>
            </div>

            <p class="lead">國道2號大竹交流道改善工程於 <strong>11/3 正式動土</strong>，這項總經費高達 <strong class="text-danger">13億5,900萬元</strong> 的重大工程，完工後將可：</p>

            <div class="alert alert-light mt-3">
              <ul class="mb-0">
                <li>✅ <strong>紓解長期匝道壅塞問題</strong></li>
                <li>✅ <strong>縮短尖峰通勤時間近一半</strong></li>
                <li>✅ <strong>全面提升桃園西區交通效率與生活便利</strong></li>
              </ul>
            </div>

            <h3 class="mt-5">🏗 工程重點亮點</h3>

            <h4>🔸 新建高架匝道橋</h4>
            <p>跨越<strong>市道110甲（中正東路）</strong>，讓車流不再塞在平交口。</p>

            <h4>🔸 南青路與側車道拓寬同步進行</h4>
            <p>由<strong>航空城工程處</strong>施工，預計 <strong>115 年 2 月完工</strong>，銜接主體工程、形成完整路網。</p>

            <h4>🔸 中央＋地方攜手合作</h4>
            <p><strong>交通部</strong>與<strong>市府</strong>共同推動，展現「<strong class="text-success">無感施工、有感竣工</strong>」的執行力。</p>

            <h3 class="mt-5">🚦 城市格局全面升級</h3>

            <p>這項工程是<strong>桃園西部交通的關鍵拼圖</strong>，將串聯：</p>

            <div class="alert alert-info mt-3">
              <ul class="mb-0">
                <li>🏗 <strong>國道1號中豐交流道</strong></li>
                <li>🏗 <strong>國道3號八德交流道</strong></li>
                <li>🏗 <strong>國道1甲、2甲延伸工程</strong></li>
              </ul>
            </div>

            <p class="lead mt-3">形成完整的「<strong class="text-primary">桃園都會環線交通網</strong>」，總投資金額超過 <strong class="text-danger">1,100 億元</strong>。</p>

            <h3 class="mt-5">🌇 長發觀點｜從交通節點走向城市核心</h3>

            <div class="alert alert-warning mt-3">
              <p>每一條道路、每一座橋樑，都是<strong>城市競爭力的具體象徵</strong>。</p>
              <p class="mb-0"><strong>大竹交流道改善工程</strong>不只是交通升級，更是桃園「<strong>空港經濟 × 城市生活圈</strong>」的重要啟動點。</p>
            </div>

            <p class="mt-3">未來的<strong>大園、南崁、航空城產專區</strong>，都將因這條交通動脈而迎來嶄新契機。</p>

            <div class="alert alert-danger mt-5">
              <h3 class="text-center">💡 交通升級 = 區域價值升級</h3>
              <p class="text-center mb-0">
                長發地產持續為您追蹤桃園重大建設動態，<br>
                幫助您掌握城市發展脈絡，精準布局未來。
              </p>
            </div>

            <div class="alert alert-light mt-4">
              <h5>🏢 長發地產｜專業服務</h5>
              <p class="mb-0">
                <strong>地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>電話：</strong>03-3568268<br>
                <strong>服務項目：</strong>重大建設追蹤、區域價值分析、土地投資諮詢
              </p>
            </div>

            <p class="mt-4 text-center lead"><strong>🚀 跟著建設走，投資不會錯｜長發地產與您同行</strong></p>
          `
        },
        'news-15': {
          category: 'news',
          date: '2025.11.03',
          title: '桃園航空城｜又一重大工程正式啟動招標！',
          content: `
            <h2>✈️ 桃園航空城｜又一重大工程正式啟動招標！</h2>
            
            <p class="text-muted"><small>來源：長發地產 | 2025-11-03</small></p>

            <div class="alert alert-success mt-4">
              <h3 class="text-center">桃園航空城核心建設持續推進！</h3>
            </div>

            <p class="lead">桃園市政府航空城工程處代辦交通部民航局的</p>

            <h3 class="mt-3">📍「桃園航空城計畫區段徵收工程（自貿港、崙仔後及A3周邊）」</h3>

            <p>已於 <strong>114年10月28日</strong> 正式刊登招標公告，總工程經費達 <strong class="text-danger">新台幣33億1,648萬5,350元</strong>，預計於 <strong>114年12月2日上午10:30</strong> 於桃園市政府802會議室開標。</p>

            <h3 class="mt-5">🗺️ 工程範圍｜三大區域 95 公頃基礎建設</h3>

            <div class="alert alert-info mt-3">
              <h4>本次工程範圍涵蓋三大區域：</h4>
              <ul class="mb-0">
                <li>🔹 <strong>自貿港區：</strong>58.39 公頃</li>
                <li>🔹 <strong>崙仔後區：</strong>7.26 公頃</li>
                <li>🔹 <strong>A3 周邊區：</strong>29.84 公頃</li>
              </ul>
            </div>

            <p class="lead mt-3">合計約 <strong class="text-primary">95 公頃</strong> 基地基礎工程</p>

            <h3 class="mt-5">🏗️ 工程內容與工期</h3>

            <div class="alert alert-light mt-3">
              <h4>工程項目包含：</h4>
              <ul class="mb-0">
                <li>✓ 整地工程</li>
                <li>✓ 道路工程</li>
                <li>✓ 排水工程</li>
                <li>✓ 橋梁工程</li>
                <li>✓ 共同管道</li>
                <li>✓ 景觀工程</li>
              </ul>
            </div>

            <p class="mt-3"><strong>工期：</strong><span class="text-danger">1,150 日曆天</span>（約3年2個月）</p>

            <h3 class="mt-5">📊 航空城整體開發進度</h3>

            <p>自<strong>111年</strong>起，桃園航空城區段徵收總面積約 <strong class="text-primary">1,756 公頃</strong>，分為<strong>10個分區</strong>陸續開發：</p>

            <div class="alert alert-success mt-3">
              <ul class="mb-0">
                <li>✅ <strong>優先開發區：</strong>已交付土地並有居民開始自建</li>
                <li>🚧 <strong>其餘區域：</strong>於113年陸續動工</li>
                <li>🎯 <strong>全區完工時程：</strong>預計於 <strong class="text-danger">119年完工</strong></li>
              </ul>
            </div>

            <h3 class="mt-5">🌆 城市發展願景｜宜居、宜業、宜發展</h3>

            <p>隨著航空城核心建設持續推進，<strong>公共設施、交通動脈與產業腹地</strong>正逐步成形，為桃園奠定「<strong class="text-success">宜居、宜業、宜發展</strong>」的城市基礎。</p>

            <div class="alert alert-warning mt-4">
              <h4>🔑 三大發展核心：</h4>
              <ul class="mb-0">
                <li>🏙️ <strong>公共設施完善：</strong>道路、排水、景觀系統化建設</li>
                <li>🚗 <strong>交通動脈串聯：</strong>機場、高鐵、捷運、國道全方位連結</li>
                <li>🏭 <strong>產業腹地成形：</strong>自貿港、產業專用區帶動就業與經濟</li>
              </ul>
            </div>

            <h3 class="mt-5">🏢 長發地產｜與城市共榮，見證桃園下一個十年</h3>

            <div class="alert alert-danger mt-3">
              <p class="mb-0">
                <strong>長發地產</strong> 持續關注航空城發展脈動，以專業角度解析重大建設、掌握區域潛能，<br>
                與城市共榮，<strong class="text-danger">見證桃園的下一個十年</strong>。
              </p>
            </div>

            <div class="alert alert-light mt-4">
              <h5>💼 長發地產專業服務</h5>
              <p class="mb-0">
                <strong>📍 地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>📞 電話：</strong>03-3568268<br>
                <strong>🎯 服務項目：</strong>航空城開發追蹤、區段徵收諮詢、土地投資規劃
              </p>
            </div>

            <p class="mt-4 text-center lead"><strong>✈️ 航空城開發持續進行，區域價值持續提升｜長發地產陪您掌握先機</strong></p>
          `
        },
        'news-12': {
          category: 'news',
          date: '2025.11.14',
          title: '經國特區 × G12 G13 G14｜啟動桃園黃金10年',
          content: `
            <h2>🚇 經國特區 × G12 G13 G14｜啟動桃園黃金10年</h2>
            <h3>國道門戶 × 新行政軸線 × 水岸生活圈</h3>
            
            <p class="text-muted"><small>來源：長發地產 | 2025-11-14</small></p>

            <h3>1️⃣ 位置價值：北桃園最關鍵的交通節點 🚗🚉</h3>

            <p>位於桃園區與蘆竹交界的經國特區，是<strong>北桃園向外擴張的交通門戶</strong>。</p>

            <div class="alert alert-info mt-3">
              <h4>三大交通優勢清晰明確：</h4>
              <ul class="mb-0">
                <li>🚗 <strong>國道一號南崁交流道</strong>：串聯雙北、桃園、青埔、中壢。</li>
                <li>🚌 <strong>經國轉運站</strong>：國道客運匯集，北桃園重要樞紐。</li>
                <li>🚉 <strong>捷運綠線 G12</strong>：未來接軌機捷藍線與台鐵紅線路網。</li>
              </ul>
            </div>

            <p class="lead mt-3">上交流道進高速路網、下橋進藝文特區、未來一站進捷運網。</p>

            <h3>2️⃣ 從工業邊陲到城市核心：經國特區的發展脈絡 🏙️</h3>

            <p>過去是工業與倉儲，現在是桃園城市升級的關鍵舞台。</p>

            <h4>三大轉折力量：</h4>

            <div class="alert alert-success mt-3">
              <ul class="mb-0">
                <li><strong>① 產業轉型</strong> → 中油遷廠後，產業空間全面重整。</li>
                <li><strong>② 人口與企業北移</strong> → 成為桃園中樞與北桃園間的結點。</li>
                <li><strong>③ 市府政策帶動</strong> → 規劃為下一階段行政與商業核心。</li>
              </ul>
            </div>

            <p class="mt-3"><strong>經國特區的價值，是城市結構變動的象徵。</strong></p>

            <h3>3️⃣ 區域現況：住商雙軸成形，機能快速到位 🏢🏠</h3>

            <h4>🏠 住宅區：水岸＋公園的宜居聚落</h4>
            <p>水岸住宅逐步成形，房價從 3 字頭上升至 5 字頭，生活氛圍成熟。</p>

            <h4>🏢 商業軸線：經國路 × 春日路成為主街道</h4>
            <p>醫療、餐飲、量販店、商辦多點開花，北桃園生活性最強區塊之一。</p>

            <h4>🏗️ 指標建商進駐</h4>
            <p><strong>璞園、中悅、忠泰、遠雄、嘉璟、嵿富</strong>等品牌連續插旗，加速區域形象成長。</p>

            <h3>4️⃣ 南崁溪的城市價值：不是河景，是生活方式 🌿🌊</h3>

            <p>南崁溪對經國特區的意義遠超過「景觀」兩字。</p>

            <div class="alert alert-light mt-3">
              <h4>🌊 生態整治後，水岸全面升級</h4>
              <p>從排水功能升級為兼具生態、防洪、景觀的城市綠帶。</p>

              <h4>🚶‍♂️🚴 21 公里親水生活軸線</h4>
              <p>河岸步道、自行車道、公園連結成桃園少有的長型生活廊道。</p>

              <h4>🏞️ 水岸住宅成為區域識別度最高的產品</h4>
              <p class="mb-0">能兼具交通便利＋水岸生活的重劃區，在北桃園極為罕見。</p>
            </div>

            <h3>5️⃣ 綠線G12・G13・G14 與行政核心：決定未來十年高度 🚇🏛️</h3>

            <h4>🚇 TOD 開發效應</h4>
            <p>車站周邊商業、住宅、公共設施將更高密度整合。</p>

            <h4>🏛️ 第二行政園區（評估中）</h4>
            <p>G12 一帶被視為具腹地＋交通優勢的候選位置。</p>

            <div class="alert alert-warning mt-3">
              <p><strong>若落腳於此，將帶來：</strong></p>
              <ul class="mb-0">
                <li>行政機能集中</li>
                <li>大型商辦與企業總部更積極布局</li>
                <li>北桃園 CBD 雛型加速成形</li>
              </ul>
            </div>

            <h4 class="mt-4">🏙️ 北桃園新 CBD 的成形關鍵</h4>
            <p class="lead"><strong>交通 × 行政 × 商業 × 水岸 → 形成經國特區的長線核心價值。</strong></p>

            <div class="alert alert-danger mt-5">
              <h3 class="text-center">💥 長發地產將「經國特區」視為桃園未來十年最重要的城市舞台</h3>
              <h4 class="text-center mt-3">✨ 城市轉型的起點，就在經國特區 ✨</h4>
            </div>

            <h3 class="mt-5">🔍 長發地產的專業服務</h3>

            <ul>
              <li>🔍 深度解析區段徵收、市地重劃、產專用地規劃</li>
              <li>🚇 第一線追蹤 捷運綠線G12・G13・G14 區段徵收與新行政園區議題</li>
              <li>🧭 以企業主、土地持有人、投資人三種視角提供布局策略</li>
            </ul>

            <p class="lead mt-4"><strong>土地的價值，不只是等待城市成長，而是參與城市成長。</strong></p>

            <div class="alert alert-success mt-4 text-center">
              <h4>桃園的起點從經國開始‼️</h4>
              <h4>長發的起點從經國開始🎉</h4>
              <h4 class="mt-3">新起點 · 新篇章 · 正式啟航🎉</h4>
              <h4 class="mt-3 text-danger">土地價值，由長發地產來創造❤️</h4>
            </div>

            <div class="alert alert-light mt-4">
              <h5>🏢 長發地產｜經國特區據點</h5>
              <p class="mb-0">
                <strong>地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>電話：</strong>03-3568268<br>
                <strong>服務項目：</strong>土地開發、區段徵收、市地重劃諮詢
              </p>
            </div>
          `
        },
        'news-1': {
          category: 'news',
          date: '2025.10.19',
          title: '結合航空城！桃園蘆竹誠聖段800戶社宅決標 2027年動工',
          content: `
            <h2>結合航空城！桃園蘆竹誠聖段800戶社宅決標 2027年動工</h2>
            
            <p class="text-muted"><small>來源：聯合新聞網 | 記者朱冠諭／桃園報導 | 2025-10-19</small></p>

            <p>桃園市人口增長快速，市府持續推動社會住宅，其中「蘆竹區誠聖段社宅」專案管理和規畫案在近期決標，預計提供近800戶社宅。住宅處長陳智仁表示，此案是結合航空城發展的指標案，預計2027年動工，未來蘆竹區社宅總量將達1987戶，盼透過社宅整合居住、社福與商業機能。</p>

            <h3>交通便捷，鄰近機場捷運</h3>
            <p>住宅處表示，誠聖段社宅是市府在蘆竹推動的重點案場，位於桃園國際機場園區特定區計畫範圍內，距離機場捷運A11坑口站僅600公尺，交通便捷，基地面積約2公頃。</p>

            <h3>多元戶型規劃</h3>
            <p>此案規畫約790戶社宅，戶型配置如下：</p>
            <ul>
              <li><strong>一房型：50%</strong> - 主要提供給青年族群</li>
              <li><strong>二房型：30%</strong> - 滿足小家庭需求</li>
              <li><strong>三房型：20%</strong> - 適合多口家庭與多元族群</li>
            </ul>

            <h3>整合社福與商業機能</h3>
            <p>誠聖段社宅除一般住宅空間，也納入多項公共設施：</p>
            <ul>
              <li>公共托育中心</li>
              <li>身心障礙者社區居住據點</li>
              <li>約900坪的商業空間，提升周邊航空城生活圈服務水準</li>
            </ul>

            <h3>高品質綠建築</h3>
            <p>全案採用先進技術與設計標準：</p>
            <ul>
              <li>通用化設計</li>
              <li>導入BIM技術</li>
              <li>取得鑽石級綠建築標章</li>
              <li>合格級智慧建築標章</li>
              <li>建築能效1+標章</li>
            </ul>

            <h3>桃園社宅發展願景</h3>
            <p>住宅處表示，目前全市已完工14處社宅、另16處啟動規畫中，明年前還會再啟動8處社宅，朝<strong>2030年提供1萬戶以上社宅</strong>的目標前進。</p>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>預計提供：約790戶社宅</li>
                <li>預計動工：2027年</li>
                <li>地點：蘆竹區，距離機捷A11站600公尺</li>
                <li>基地面積：約2公頃</li>
                <li>特色：結合航空城發展、整合居住與社福機能</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://news.housefun.com.tw/news/article/522306475384.html" target="_blank">好房網News - 聯合新聞網</a></small></p>
          `
        },
        'news-2': {
          category: 'news',
          date: '2025.10.20',
          title: '航空城拆遷戶貸款卡關，是被信用管制措施害慘了？央行最新回應出爐',
          content: `
            <h2>航空城拆遷戶貸款卡關，是被信用管制措施害慘了？央行最新回應出爐</h2>
            
            <p class="text-muted"><small>來源：Yahoo奇摩新聞 | 2025-10-20</small></p>

            <p>近期有報導指出，桃園航空城拆遷戶在申請貸款時遇到困難，部分民眾質疑是否因中央銀行的信用管制措施所致，引發各界關注。對此，央行已做出最新回應澄清相關疑慮。</p>

            <h3>拆遷戶貸款困境</h3>
            <p>桃園航空城計畫涉及大規模土地徵收與拆遷作業，許多拆遷戶在取得補償金後，希望能購置新房。然而，部分民眾反映在申請房貸時遭遇困難，擔心是否受到央行信用管制政策影響。</p>

            <h3>央行最新回應</h3>
            <p>針對外界質疑，中央銀行澄清：</p>
            <ul>
              <li><strong>信用管制措施並未針對一般民眾</strong> - 房市信用管制主要針對投資客與多屋族，並非限制自住需求</li>
              <li><strong>未排除拆遷戶的貸款需求</strong> - 拆遷戶若符合自住條件，仍可依正常程序申請房貸</li>
              <li><strong>銀行應依個案審核</strong> - 各金融機構應依借款人實際情況與還款能力進行評估</li>
            </ul>

            <h3>銀行審核考量因素</h3>
            <p>金融業者表示，拆遷戶貸款申請時，銀行會綜合考量以下因素：</p>
            <ul>
              <li>借款人的信用紀錄與還款能力</li>
              <li>收入來源的穩定性</li>
              <li>負債比例</li>
              <li>購屋目的（自住或投資）</li>
              <li>補償金來源證明</li>
            </ul>

            <h3>專家建議</h3>
            <p>房地產專家建議拆遷戶在申請貸款時：</p>
            <ul>
              <li>準備完整的財力證明文件</li>
              <li>說明補償金來源與購屋目的</li>
              <li>保持良好的信用紀錄</li>
              <li>可諮詢多家銀行，比較貸款條件</li>
              <li>必要時尋求專業代書或貸款顧問協助</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點提醒</strong>
              <ul class="mb-0">
                <li>央行信用管制未針對拆遷戶自住需求</li>
                <li>銀行應依個案實際情況審核貸款申請</li>
                <li>拆遷戶應備妥完整財力證明文件</li>
                <li>建議多方比較不同銀行的貸款方案</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://tw.news.yahoo.com/航空城拆遷戶貸款卡關-是被信用管制措施害慘了-央行最新回應出爐-105620733.html" target="_blank">Yahoo奇摩新聞</a></small></p>
          `
        },
        'news-3': {
          category: 'news',
          date: '2025.02.25',
          title: '桃園航空城計畫重大進度曝！「先建後遷」期程出爐、不再展延',
          content: `
            <h2>桃園航空城計畫重大進度曝！「先建後遷」期程出爐、不再展延</h2>
            
            <p class="text-muted"><small>來源：住展雜誌 | 2025-02-25</small></p>

            <p>行政院政務委員陳金德率相關部會前往桃園航空城，召開第25次專案小組委員會議，強調「先建後遷」期程將不再展延，務必於2026年底前完成，以利第三跑道工程依計畫推進。</p>

            <h3>「先建後遷」政策確立</h3>
            <p>桃園航空城計畫採取「先建後遷」策略，確保拆遷戶有安置住所後才進行拆遷作業。此政策旨在保障居民權益，減少拆遷過程中的不便與衝突。</p>

            <h3>重要時程節點</h3>
            <ul>
              <li><strong>2025年1月</strong> - 安置街廓已啟動點交作業</li>
              <li><strong>2025年2月</strong> - 安置住宅陸續取得使用執照並驗交屋</li>
              <li><strong>2026年底前</strong> - 完成「先建後遷」期程，不再展延</li>
              <li><strong>後續</strong> - 配合第三跑道工程依計畫推進</li>
            </ul>

            <h3>安置住宅現況</h3>
            <p>目前航空城安置住宅建設進度如下：</p>
            <ul>
              <li>多處安置街廓已完工並取得使用執照</li>
              <li>拆遷戶陸續進行驗屋與交屋程序</li>
              <li>安置住宅品質符合現代居住標準</li>
              <li>周邊生活機能逐步完善</li>
            </ul>

            <h3>第三跑道工程規劃</h3>
            <p>桃園國際機場第三跑道是航空城計畫的核心建設之一：</p>
            <ul>
              <li>提升機場運能，強化台灣航空樞紐地位</li>
              <li>配合國家整體經濟發展策略</li>
              <li>創造就業機會與產業發展契機</li>
              <li>完善周邊交通與生活機能</li>
            </ul>

            <h3>政策不再展延的意義</h3>
            <p>行政院強調期程不再展延，顯示政府推動航空城計畫的決心：</p>
            <ul>
              <li><strong>提升執行效率</strong> - 確保各項工程按時完成</li>
              <li><strong>保障居民權益</strong> - 讓拆遷戶盡早安定生活</li>
              <li><strong>促進區域發展</strong> - 加速航空城整體建設進程</li>
              <li><strong>強化國際競爭力</strong> - 提升台灣航空產業地位</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>「先建後遷」期程：2026年底前完成，不再展延</li>
                <li>安置住宅：已啟動點交與驗屋作業</li>
                <li>第三跑道：配合安置進度依計畫推進</li>
                <li>政策目標：保障居民權益、提升機場運能</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://www.myhousing.com.tw/n/n01/north-taiwan/taoyuan-estate/195740/" target="_blank">住展雜誌</a></small></p>
          `
        },
        'news-4': {
          category: 'news',
          date: '2024.09.06',
          title: '桃園捷運綠線G12站土地開發計畫啟動',
          content: `
            <h2>桃園捷運綠線G12站土地開發計畫啟動</h2>
            
            <p class="text-muted"><small>來源：客家新聞 | 2024-09-06</small></p>

            <p>桃園捷運綠線G12站周邊土地開發計畫正式啟動，預期將帶動中壢地區發展，為當地房地產市場注入新動能。</p>

            <h3>G12站區位優勢</h3>
            <p>G12站位於中壢核心區域，具備以下優勢：</p>
            <ul>
              <li>緊鄰中壢商業區，生活機能完善</li>
              <li>串聯桃園國際機場與高鐵站，交通便捷</li>
              <li>周邊有多所學校，教育資源豐富</li>
              <li>連接科技園區，就業機會多元</li>
            </ul>

            <h3>土地開發規劃</h3>
            <p>本次開發計畫包含以下重點項目：</p>
            <ul>
              <li><strong>TOD 開發</strong> - 以捷運站為中心的複合式開發</li>
              <li><strong>住商混合</strong> - 規劃住宅、商業、辦公等多元用途</li>
              <li><strong>公共設施</strong> - 完善的綠地、公園與社區空間</li>
              <li><strong>智慧城市</strong> - 導入智慧化管理系統</li>
            </ul>

            <h3>對區域發展的影響</h3>
            <p>G12站周邊土地開發將帶來多重效益：</p>
            <ul>
              <li>提升中壢地區的整體形象與競爭力</li>
              <li>帶動周邊房地產增值</li>
              <li>創造就業機會，促進地方經濟發展</li>
              <li>改善交通環境，提升生活品質</li>
              <li>吸引人口移入，活絡地方發展</li>
            </ul>

            <h3>預期時程</h3>
            <p>根據規劃，開發計畫將分階段進行：</p>
            <ul>
              <li>第一階段：都市計畫變更與土地整合</li>
              <li>第二階段：基礎建設與公共設施建置</li>
              <li>第三階段：建築開發與招商作業</li>
              <li>預計於捷運綠線完工後全面啟動</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>地點：桃園捷運綠線G12站周邊</li>
                <li>開發模式：TOD 複合式開發</li>
                <li>預期效益：提升區域價值與生活品質</li>
                <li>配合時程：2026年捷運綠線通車</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://hakkanews.tw/2024/09/06/141644/" target="_blank">客家新聞</a></small></p>
          `
        },
        'news-5': {
          category: 'news',
          date: '2024.09.06',
          title: '桃園捷運綠線機廠工程進度超前',
          content: `
            <h2>桃園捷運綠線機廠工程進度超前</h2>
            
            <p class="text-muted"><small>來源：中央社 | 2024-09-06</small></p>

            <p>桃園捷運綠線機廠工程進度順利，預計2026年完工通車。機廠將提供列車維修與停放功能，是綠線營運的重要設施。</p>

            <h3>機廠功能與規模</h3>
            <p>捷運綠線機廠是營運系統的核心設施，主要功能包括：</p>
            <ul>
              <li><strong>列車停放</strong> - 提供充足的列車停放空間</li>
              <li><strong>維修保養</strong> - 配備完整的維修設備與技術團隊</li>
              <li><strong>車輛檢修</strong> - 定期進行安全檢測與保養作業</li>
              <li><strong>調度管理</strong> - 統籌列車運行調度</li>
            </ul>

            <h3>工程進度與特色</h3>
            <p>機廠工程展現以下特點：</p>
            <ul>
              <li>採用綠建築設計，節能環保</li>
              <li>導入智慧化管理系統</li>
              <li>工程進度超前預定時程</li>
              <li>品質管控嚴謹，確保安全</li>
              <li>周邊景觀美化，融入社區</li>
            </ul>

            <h3>對捷運綠線的重要性</h3>
            <p>機廠完工對捷運綠線營運至關重要：</p>
            <ul>
              <li>確保列車維持最佳運行狀態</li>
              <li>提升運輸安全與服務品質</li>
              <li>支援未來路網擴充需求</li>
              <li>創造就業機會，帶動地方發展</li>
            </ul>

            <h3>2026年通車目標</h3>
            <p>桃園捷運綠線預計2026年通車，機廠將扮演關鍵角色：</p>
            <ul>
              <li>提供完善的後勤支援</li>
              <li>確保營運順暢穩定</li>
              <li>支持長期永續經營</li>
              <li>提升整體運輸效率</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>機廠工程進度超前預定時程</li>
                <li>採用綠建築與智慧化設計</li>
                <li>配合2026年綠線通車目標</li>
                <li>為營運提供完整後勤支援</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://www.cna.com.tw/news/aloc/202409060204.aspx" target="_blank">中央社</a></small></p>
          `
        },
        'news-6': {
          category: 'news',
          date: '2024.09.05',
          title: '台鐵中路站周邊都市計畫啟動，124公頃土地開發',
          content: `
            <h2>台鐵中路站周邊都市計畫啟動，124公頃土地開發</h2>
            
            <p class="text-muted"><small>來源：好房網News | 2024-09-05</small></p>

            <p>台鐵中路站周邊124公頃土地開發計畫正式啟動，將成為桃園重要發展區域，結合鐵路運輸與都市發展優勢，打造全新生活圈。</p>

            <h3>中路站發展潛力</h3>
            <p>台鐵中路站具備優越的發展條件：</p>
            <ul>
              <li>位於桃園市核心區域，交通便捷</li>
              <li>鄰近桃園火車站，串聯雙鐵路網</li>
              <li>周邊有完善的生活機能與商業發展</li>
              <li>未來將結合捷運綠線，形成立體交通網</li>
            </ul>

            <h3>124公頃開發規劃</h3>
            <p>本次都市計畫涵蓋廣大區域，規劃內容包括：</p>
            <ul>
              <li><strong>住宅區</strong> - 規劃多元住宅型態，滿足不同需求</li>
              <li><strong>商業區</strong> - 發展商業、辦公與服務產業</li>
              <li><strong>公共設施</strong> - 完善學校、公園、綠地等設施</li>
              <li><strong>交通建設</strong> - 改善道路系統，強化聯外交通</li>
              <li><strong>TOD 開發</strong> - 以車站為中心的複合式開發</li>
            </ul>

            <h3>對區域發展的影響</h3>
            <p>中路站周邊開發將帶來多重效益：</p>
            <ul>
              <li>提升桃園市整體都市形象</li>
              <li>創造大量就業機會</li>
              <li>帶動周邊房地產增值</li>
              <li>完善生活機能，提高居住品質</li>
              <li>吸引人口與產業進駐</li>
              <li>促進地方經濟繁榮發展</li>
            </ul>

            <h3>開發時程與願景</h3>
            <p>都市計畫將分階段推動：</p>
            <ul>
              <li>第一階段：都市計畫變更與審議</li>
              <li>第二階段：土地徵收與整合</li>
              <li>第三階段：基礎建設施工</li>
              <li>第四階段：招商引資與建築開發</li>
              <li>目標：打造桃園新興發展核心</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>開發面積：124公頃</li>
                <li>地點：台鐵中路站周邊</li>
                <li>開發模式：TOD 複合式開發</li>
                <li>願景：打造桃園新興發展核心</li>
                <li>預期效益：提升區域價值與生活品質</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://news.housefun.com.tw/news/article/168960454936.html" target="_blank">好房網News</a></small></p>
          `
        },
        'news-7': {
          category: 'news',
          date: '2025.01.06',
          title: '桃園都市更新獎勵加碼，防災型重建最高獎勵容積50%',
          content: `
            <h2>桃園都市更新獎勵加碼，防災型重建最高獎勵容積50%</h2>
            
            <p class="text-muted"><small>來源：都市更新入口網 | 2025-01-06</small></p>

            <p>桃園市政府都市發展局修正「都市計畫法桃園市施行細則」，本週經市政會議審議通過，將函報內政部審議，預計2025年上半年發布實施。本次修法重點包括提高防災型都更重建獎勵容積、新增可負擔住宅法源依據，以及推動淨零桃園政策。</p>

            <h3>三大修法重點</h3>
            <ul>
              <li><strong>強化社宅政策</strong> - 新增「可負擔住宅」的法源依據，協助青年安居</li>
              <li><strong>提高防災型都更誘因</strong> - 最高獎勵容積達50%，加速舊城再生</li>
              <li><strong>推動淨零桃園</strong> - 增訂垂直綠化等容積計算與獎勵方式</li>
            </ul>

            <h3>防災型都更獎勵詳情</h3>
            <p>為加速老舊建築更新，桃園市府大幅提高防災型都更重建獎勵：</p>
            <ul>
              <li>基準容積獎勵：最高可達50%</li>
              <li>適用對象：符合耐震能力不足或有立即危險之老舊建築</li>
              <li>簡化程序：縮短審查時程，加速都更進程</li>
              <li>配套措施：提供專業輔導團隊協助申請</li>
            </ul>

            <h3>可負擔住宅新制</h3>
            <p>桃園市首創「可負擔住宅」制度：</p>
            <ul>
              <li>定義：價格合理、青年可負擔的住宅產品</li>
              <li>目標：協助青年世代在地安居</li>
              <li>配合捷運：優先於捷運沿線規劃</li>
              <li>管理機制：建立長期追蹤管理制度</li>
            </ul>

            <h3>淨零綠化獎勵</h3>
            <p>配合2050淨零排放目標，新增綠化獎勵措施：</p>
            <ul>
              <li>垂直綠化：立面綠化可計入綠覆率</li>
              <li>容積獎勵：達到綠化標準可獲得額外容積</li>
              <li>環保設施：鼓勵設置雨水回收、太陽能等設施</li>
              <li>智慧建築：導入智慧化管理系統</li>
            </ul>

            <h3>對房市的影響</h3>
            <p>本次修法預期將帶來以下影響：</p>
            <ul>
              <li>加速老舊社區都更進度</li>
              <li>提升建築安全品質</li>
              <li>增加可負擔住宅供給</li>
              <li>促進綠建築發展</li>
              <li>帶動營建產業發展</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>防災型都更最高獎勵容積50%</li>
                <li>新增可負擔住宅法源依據</li>
                <li>推動淨零綠化獎勵措施</li>
                <li>預計2025年上半年實施</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://urbanrenewal.wealth.com.tw/news-detail/1932" target="_blank">都市更新入口網</a></small></p>
          `
        },
        'news-8': {
          category: 'news',
          date: '2024.08.20',
          title: '桃園小檜溪重劃區房價飆漲，吸引首購族進駐',
          content: `
            <h2>桃園小檜溪重劃區房價飆漲，吸引首購族進駐</h2>
            
            <p class="text-muted"><small>來源：好房網News | 2024-08-20</small></p>

            <p>桃園市小檜溪重劃區因交通便利、生活機能完善，近年房價持續上漲，成為首購族青睞的新興熱門區域。</p>

            <h3>小檜溪重劃區優勢</h3>
            <p>小檜溪重劃區位於桃園市核心區域，具備多項優勢：</p>
            <ul>
              <li><strong>交通便捷</strong> - 鄰近國道交流道，往來台北、新竹便利</li>
              <li><strong>生活機能完善</strong> - 周邊商圈、學校、醫療設施齊全</li>
              <li><strong>環境優質</strong> - 規劃完整的公園綠地與人行步道</li>
              <li><strong>發展潛力大</strong> - 未來將有捷運等重大建設</li>
            </ul>

            <h3>房價走勢分析</h3>
            <p>小檜溪重劃區房價近年持續攀升：</p>
            <ul>
              <li>新成屋平均單價：每坪28-32萬元</li>
              <li>中古屋平均單價：每坪24-28萬元</li>
              <li>年漲幅：約8-10%</li>
              <li>相較桃園市區：價格相對親民</li>
            </ul>

            <h3>首購族青睞原因</h3>
            <p>小檜溪重劃區受首購族歡迎的主要原因：</p>
            <ul>
              <li><strong>價格合理</strong> - 總價相對親民，首購族負擔得起</li>
              <li><strong>環境新穎</strong> - 重劃區規劃完整，居住品質佳</li>
              <li><strong>增值潛力</strong> - 區域持續發展，保值性高</li>
              <li><strong>貸款條件</strong> - 新屋貸款成數較高，利率優惠</li>
            </ul>

            <h3>未來發展規劃</h3>
            <p>小檜溪重劃區未來發展展望：</p>
            <ul>
              <li>捷運綠線延伸線規劃中</li>
              <li>大型商場與購物中心陸續進駐</li>
              <li>學區持續完善</li>
              <li>公共設施逐步到位</li>
            </ul>

            <h3>專家建議</h3>
            <p>房地產專家對首購族的建議：</p>
            <ul>
              <li>選擇交通便利的地段</li>
              <li>注意周邊生活機能</li>
              <li>評估未來發展潛力</li>
              <li>考量自身財務能力</li>
              <li>實地看屋，了解周邊環境</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>新成屋單價：每坪28-32萬元</li>
                <li>年漲幅：約8-10%</li>
                <li>優勢：交通便利、生活機能完善</li>
                <li>適合首購族進駐</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://news.housefun.com.tw/news/article/103520474579.html" target="_blank">好房網News</a></small></p>
          `
        },
        'news-18': {
          category: 'news',
          date: '2026.05.27',
          title: '三鶯線延伸八德工程啟動 張善政：123年朝通車目標邁進',
          thumbnail: '/news-18.jpg',
          content: `
            <h2>三鶯線延伸八德工程啟動 張善政：123年朝通車目標邁進</h2>

            <p class="text-muted"><small>來源：長發地產 | 2026-05-27</small></p>

            <img src="/news-18.jpg" alt="三鶯線延伸八德工程" class="img-fluid rounded mb-4" style="width:100%;max-width:600px;display:block;margin:0 auto 1.5rem;" />

            <p>桃園與新北捷運路網整合再邁新里程，捷運三鶯線延伸八德計畫基本設計工程總經費160.57億元，已獲行政院公共工程委員會審議通過，正式進入工程招標及實質推動階段。桃園市長 張善政 今（27）日上午主持市政會議時表示，延伸段未來將與桃園捷運綠線G04大湳站共構銜接，串聯桃園與雙北軌道路網，市府將持續與新北市政府深化合作，朝民國123年通車目標穩步邁進。</p>

            <p>張善政指出，三鶯線主線工程目前已接近完工，並完成列車測試及初勘作業，5月已提報交通部辦理履勘。未來延伸段通車後，桃園市民可透過三鶯線銜接台北捷運板南線等幹線路網，提升往返雙北通勤效率；新北三峽、鶯歌地區居民，也能轉乘桃園捷運綠線，直達八德、桃園市區、青埔及桃園國際機場等重要節點，強化北北桃共同生活圈交通聯結。</p>

            <h3 class="mt-4">工程推動進度</h3>

            <p>張市長表示，本計畫由桃園市政府與新北市政府共同推動，並委由新北市捷運工程局代辦規劃設計及施工。新北市捷運工程局已於經費審議通過當日完成高架段細部設計開標，地下段土建統包標及全線機電系統標則預計於6月上網招標，工程推動進度持續加快。</p>

            <div class="alert alert-info mt-3">
              <h5>市府協作整合項目</h5>
              <ul class="mb-0">
                <li>提供捷運綠線既有鑽探資料與施工經驗</li>
                <li>協助電塔遷移及施工道路用地取得</li>
                <li>預埋車站水電與環控管線</li>
                <li>轉乘車站結構於綠線工程中同步施作</li>
                <li>協助延伸段施工剩餘土方媒合處置</li>
              </ul>
            </div>

            <h3 class="mt-4">路線規劃與區域效益</h3>

            <p>市府表示，三鶯線延伸八德全長約4公里，規劃設置2座高架車站及1座地下車站，完工後將有效縮短八德往返鶯歌、三峽及土城交通時間，並帶動大湳及八德周邊地區發展，進一步提升區域生活與產業機能。</p>

            <p>張市長強調，三鶯線延伸八德不僅是桃園「三心六線」捷運路網的重要環節，也將與新北「三環六線」緊密銜接，共同建構北台灣軌道交通骨幹。市府除感謝中央支持外，也會持續與新北市府密切合作，全力推進後續工程，打造更便捷、安全的通勤與生活環境。</p>

            <div class="alert alert-light mt-4">
              <h5>💼 長發地產專業服務</h5>
              <p class="mb-0">
                <strong>📍 地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>📞 電話：</strong>03-3568268<br>
                <strong>🎯 服務項目：</strong>捷運綠線沿線土地諮詢、區段徵收補償評估、土地投資規劃
              </p>
            </div>
          `
        },
        'news-17': {
          category: 'news',
          date: '2026.05.27',
          title: '🚀桃園航空城95公頃產專區｜正式進入「精準招商」階段‼️',
          thumbnail: '/news-17.jpg',
          content: `
            <h2>🚀桃園航空城95公頃產專區｜正式進入「精準招商」階段‼️</h2>

            <p class="text-muted"><small>來源：長發地產 | 2026-05-27</small></p>

            <img src="/news-17.jpg" alt="桃園航空城95公頃產專區精準招商" class="img-fluid rounded mb-4" style="width:100%;max-width:600px;display:block;margin:0 auto 1.5rem;" />

            <p>桃園市政府正式啟動：「桃園航空城計畫附近地區第一期第三種產業專用區精準招商及招商策略規劃委託專業服務案」，由 <strong>安侯企業管理（KPMG）</strong> 得標。</p>

            <p>這件事代表的，不只是招商。而是桃園航空城，正式開始進入：<strong>「產業定位規劃期」</strong>。</p>

            <h3 class="mt-4">從土地開發到產業定位</h3>

            <p>過去幾年，航空城主要推動的是：</p>
            <ul>
              <li>區段徵收</li>
              <li>公共工程</li>
              <li>基礎建設</li>
              <li>優先產專區標售</li>
            </ul>

            <p>但這次不同。從「精準招商」四個字來看，市府接下來的重點，已經開始從「土地開發」，轉向：<strong>「要引進哪些產業」</strong>。</p>

            <p>而 KPMG 的角色，也不是工程顧問公司，而是以產業、投資、財務分析為主的國際顧問團隊。代表未來這95公頃產專區，可能將同步進行：</p>

            <div class="alert alert-info mt-3">
              <ul class="mb-0">
                <li>產業定位研究</li>
                <li>國內外企業訪談</li>
                <li>投資需求調查</li>
                <li>土地配置規劃</li>
                <li>招商條件設計</li>
                <li>開發效益分析</li>
              </ul>
            </div>

            <h3 class="mt-4">「3低1高」低碳產業園區方向</h3>

            <p>桃園航空城招商明確朝向：低污染、低耗能、低用水、高附加價值的「3低1高」型態，規劃成<strong>低碳產業園區</strong>！</p>

            <div class="alert alert-success mt-3">
              <h5>產業群可能包含：</h5>
              <ul class="mb-0">
                <li>AI 供應鏈</li>
                <li>半導體設備鏈</li>
                <li>國際物流</li>
                <li>航太維修</li>
                <li>生技醫療</li>
                <li>ESG 低碳產業</li>
              </ul>
            </div>

            <h3 class="mt-4">從基礎建設到產業聚落</h3>

            <p>從市場角度來看，航空城目前已逐步從「基礎建設階段」開始走向「產業聚落形成階段」。而未來真正影響區域發展的，不只是道路與機場，更是：<strong>「哪些企業進駐」</strong>。</p>

            <p>這也將牽動後續：</p>
            <ul>
              <li>就業人口</li>
              <li>商辦需求</li>
              <li>住宅需求</li>
              <li>區域產值</li>
              <li>土地市場定位</li>
            </ul>

            <div class="alert alert-warning mt-3">
              <h5>航空城產專區追蹤進度總結</h5>
              <ul class="mb-0">
                <li><strong>114年定方向：</strong>定調低碳科技園區！</li>
                <li><strong>115年找對象：</strong>安侯企業管理（KPMG）精準招商得標！</li>
                <li><strong>116年正式啟動</strong>航空城大配地與產專區大升級！</li>
              </ul>
            </div>

            <h3 class="mt-4">資料來源</h3>
            <ul>
              <li>桃園市政府工作報告</li>
              <li>政府電子採購網</li>
              <li><a href="https://www.beraniq.com/awardbid/biddetail?bid=3439570" target="_blank">得標資訊</a></li>
            </ul>

            <p>桃園航空城，正式進入下一個階段。若想進一步了解 95公頃產專區在哪裡？航空城市府二期啟動時機？歡迎私訊 <strong>#長發地產</strong>，我們持續追蹤第一手重大建設資訊，深入解析市場趨勢與未來佈局時機。</p>

            <div class="alert alert-light mt-4">
              <h5>💼 長發地產｜北台灣土地整合專家</h5>
              <p>專營五大開發主軸：區段徵收、市地重劃、土地整合、合建買賣、都市更新</p>
              <p class="mb-0">
                <strong>📍 地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>📞 電話：</strong>03-3568268<br>
                <strong>🎯 關注重點：</strong>#航空城大配地 #捷運綠線 #G4大湳森林公園 #G12G13G14 #台鐵紅線中路站 #市府二期
              </p>
            </div>
          `
        },
        'news-16': {
          category: 'news',
          date: '2026.03.15',
          title: "區段徵收G12、G13、G14 開發案『市都委會第一次審議進度解析』",
          thumbnail: '/G121314.jpg',
          content: `
            <h2>區段徵收G12、G13、G14 開發案</h2>
            <h3>『市都委會第一次審議進度解析』</h3>

            <p class="text-muted"><small>來源：長發地產 | 2026-03-15</small></p>

            <p>📌 桃園捷運綠線沿線的土地開發，一直被視為桃園下一波城市發展的重要觀察指標。其中，位於南崁都市計畫區內的 G12、G13、G14 車站周邊土地開發案，被定位為銜接航空城、蘆竹與桃園市區的重要節點，因此長期以來在市場上備受關注。</p>

            <p>本案涉及『都市計畫變更 × 區段徵收』，程序相對複雜。真正決定開發能否推動的關鍵，不在工程，也不在徵收，而是在『都市計畫審議是否完成』。</p>

            <div class="alert alert-warning mt-3">
              <strong>只要都市計畫尚未審定，區段徵收就不可能啟動。</strong>
            </div>

            <p>而近期出現新的重要進度。</p>

            <h3 class="mt-4">📌 第一次都市計畫委員會專案小組審議</h3>

            <p>本次公展後於民國115年2月25日召開第一次『都市計畫委員會專案小組審議』，並正式產生會議紀錄。</p>

            <div class="alert alert-success mt-3">
              <p class="mb-1">這代表案件已進入</p>
              <ul class="mb-0">
                <li>👉 市都委會實質審議階段</li>
                <li>👉 新的審議周期正式開始</li>
              </ul>
            </div>

            <p class="mt-3">對熟悉都市計畫流程的人而言，第一次專案小組不是定案，而是審議的起點。</p>

            <h3 class="mt-4">📌 專案小組會議透露的訊號</h3>

            <p>從本次會議紀錄可以看出，委員關注的並非單一細節，而是『整體開發邏輯是否成熟』。</p>

            <p>只要審議仍停留在合理性與必要性的討論階段，就代表案件仍在初期，而非定案階段。</p>

            <h4 class="mt-3">一、整體開發的上位計畫與供需論證</h4>

            <p>本次專案小組要求補強的重點，首先是整體開發的上位計畫與供需論證。包括本案與國土計畫、航空城發展、捷運綠線定位，以及周邊產業與住宅需求之間的關聯性。</p>

            <div class="alert alert-info mt-3">
              <p class="mb-1">這類要求通常代表：</p>
              <ul class="mb-0">
                <li>✔ 計畫不是不能做</li>
                <li>✔ 但必須先說清楚為什麼要做</li>
              </ul>
            </div>

            <p class="mt-3">只要整體論述尚未完整，審議就不會快速推進。</p>

            <h4 class="mt-3">二、水系與防洪議題</h4>

            <p>此外，本案範圍涉及河川治理、埤塘保留與滯洪空間配置，相關水系與防洪議題仍需與中央主管機關協調。</p>

            <p>實務上，只要都市計畫牽涉『河道調整 / 防洪系統 / 水利審議』審議時間往往會拉長，這也是大型區段徵收案常見的變數之一。</p>

            <h4 class="mt-3">三、交通系統配置</h4>

            <p>交通系統配置同樣尚未完全確定。會議中要求重新檢討聯外道路、橫向動線、跨越國道方式，以及車站專用區與周邊土地使用之間的整體關係。</p>

            <div class="alert alert-warning mt-3">
              <p class="mb-0">在都市計畫實務上：<br>
              <strong>交通未定 → 配置未定 → 分區未定 → 計畫不能過</strong></p>
            </div>

            <h4 class="mt-3">四、區段徵收範圍仍在討論</h4>

            <p>同時，區段徵收範圍仍在討論之中，包括既有聚落是否保留、部分工廠與宗教設施如何處理、埤塘是否納入，以及農業區是否保留等問題。</p>

            <p>只要範圍仍可能調整，公共設施比例與整體開發配置就可能改變。</p>

            <h3 class="mt-4">📌 現地勘查安排</h3>

            <div class="alert alert-danger mt-3">
              <p class="mb-0">值得注意的是，本次會議結論要求安排<strong>『專案小組現地勘查』</strong></p>
            </div>

            <p class="mt-3">換言之115年2月25日這場專案小組所代表的訊號，雖然尚未定案，但訊號強烈表示G12、G13、G14 區段徵收審議已正式啟動‼️</p>

            <h3 class="mt-4">📌 ＃長發觀點</h3>

            <div class="alert alert-light border mt-3">
              <p class="mb-0">我們以持續追蹤審議進度為投資核心，透過解讀都市計畫會議內容，掌握政策修正方向，提供市場即時資訊，提升土地投資判斷的穩定度。</p>
            </div>

            <div class="alert alert-light mt-4">
              <h5>💼 長發地產專業服務</h5>
              <p class="mb-0">
                <strong>📍 地址：</strong>桃園市桃園區經國一路92號3樓<br>
                <strong>📞 電話：</strong>03-3568268<br>
                <strong>🎯 服務項目：</strong>G12/G13/G14開發追蹤、區段徵收諮詢、土地投資規劃
              </p>
            </div>
          `
        },
        'news-9': {
          category: 'news',
          date: '2024.10.20',
          title: '桃園捷運綠線3站要開發！近千人湧入TOD說明會',
          content: `
            <h2>桃園捷運綠線3站要開發！近千人湧入TOD說明會</h2>
            
            <p class="text-muted"><small>來源：Yahoo奇摩新聞 - 中國時報 | 2024-10-20</small></p>

            <p>桃園捷運綠線G07、G08、G09三站TOD（Transit-Oriented Development，大眾運輸導向發展）開發說明會吸引近千位民眾參與，顯示當地居民對站點開發的高度關注，預期將帶動周邊房市發展。</p>

            <h3>三站TOD開發概況</h3>
            <p>本次開發涵蓋三個捷運站：</p>
            <ul>
              <li><strong>G07站</strong> - 位於八德區，規劃住商混合開發</li>
              <li><strong>G08站</strong> - 鄰近中壢區，將打造商業核心</li>
              <li><strong>G09站</strong> - 連接桃園與中壢，發展潛力大</li>
            </ul>

            <h3>TOD開發特色</h3>
            <p>以大眾運輸為導向的複合式開發：</p>
            <ul>
              <li><strong>高密度開發</strong> - 車站周邊500公尺內集中開發</li>
              <li><strong>多元功能</strong> - 整合住宅、商業、辦公、休閒</li>
              <li><strong>步行友善</strong> - 強調人行空間與無障礙設計</li>
              <li><strong>綠色交通</strong> - 鼓勵使用大眾運輸，減少私人運具</li>
            </ul>

            <h3>說明會重點內容</h3>
            <p>說明會中市府說明的開發重點：</p>
            <ul>
              <li>開發範圍與規模說明</li>
              <li>容積移轉與獎勵機制</li>
              <li>公共設施配置計畫</li>
              <li>交通動線規劃</li>
              <li>居民權益保障措施</li>
            </ul>

            <h3>民眾關注焦點</h3>
            <p>參與說明會的民眾主要關心：</p>
            <ul>
              <li>既有住戶權益如何保障</li>
              <li>房價可能的漲幅</li>
              <li>施工期間的影響</li>
              <li>未來生活機能改善</li>
              <li>參與開發的機會</li>
            </ul>

            <h3>對房市的影響</h3>
            <p>三站TOD開發預期帶來以下影響：</p>
            <ul>
              <li>周邊房價看漲，增值潛力大</li>
              <li>吸引建商與投資客關注</li>
              <li>生活機能大幅提升</li>
              <li>商業活動更加活絡</li>
              <li>人口可能大量移入</li>
            </ul>

            <h3>開發時程</h3>
            <p>預計開發進度：</p>
            <ul>
              <li>2024年：完成規劃與說明會</li>
              <li>2025年：啟動都市計畫變更</li>
              <li>2026年：配合綠線通車，啟動開發</li>
              <li>2027-2030年：分期完成開發建設</li>
            </ul>

            <div class="alert alert-info mt-4">
              <strong>重點摘要</strong>
              <ul class="mb-0">
                <li>開發站點：G07、G08、G09三站</li>
                <li>說明會參與人數：近千人</li>
                <li>開發模式：TOD複合式開發</li>
                <li>預計時程：配合2026年綠線通車</li>
                <li>預期效益：帶動周邊房市發展</li>
              </ul>
            </div>

            <p class="mt-3"><small>資料來源：<a href="https://tw.news.yahoo.com/桃園捷運綠線3站要開發-近千人湧入說明會-041627129.html" target="_blank">Yahoo奇摩新聞 - 中國時報</a></small></p>
          `
        }
      }
    }
  },
  computed: {
    slug() {
      return this.$route.params.slug;
    },
    article() {
      return this.articles[this.slug] || {
        category: 'wiki',
        date: '',
        title: '文章不存在',
        content: '<p>找不到該文章</p>'
      };
    },
    categoryName() {
      return this.article.category === 'news' ? '新聞總覽' : '知識百科';
    },
    currentIndex() {
      return this.articlesList.findIndex(item => item.slug === this.slug);
    },
    prevArticle() {
      if (this.currentIndex > 0) {
        return this.articlesList[this.currentIndex - 1];
      }
      return null;
    },
    nextArticle() {
      if (this.currentIndex >= 0 && this.currentIndex < this.articlesList.length - 1) {
        return this.articlesList[this.currentIndex + 1];
      }
      return null;
    },
    relatedArticles() {
      // 取得同分類的其他文章，最多3篇
      const sameCategoryArticles = this.articlesList.filter(item => 
        item.slug !== this.slug && item.category === this.article.category
      );
      return sameCategoryArticles.slice(0, 3);
    }
  },
  mounted() {
    window.scrollTo(0, 0);
  },
  watch: {
    '$route'() {
      window.scrollTo(0, 0);
    }
  }
}
</script>

<style scoped>
.post-detail-page {
  min-height: 80vh;
  background: #fff;
}

.post-container {
  padding-top: 180px;
  padding-bottom: 80px;
}

.article-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #333;
  line-height: 1.3;
}

.article-content {
  font-size: 1.1rem;
  line-height: 1.8;
  color: #444;
}

.article-content h2 {
  font-size: 2rem;
  font-weight: 600;
  margin-top: 2rem;
  margin-bottom: 1rem;
  color: #222;
}

.article-content h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.8rem;
  color: #333;
}

.article-content h4 {
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  color: #444;
}

.article-content p {
  margin-bottom: 1rem;
}

.article-content ul,
.article-content ol {
  margin-bottom: 1.5rem;
  padding-left: 2rem;
}

.article-content li {
  margin-bottom: 0.5rem;
}

.article-content strong {
  color: #222;
  font-weight: 600;
}

.article-content table {
  margin: 1.5rem 0;
}

/* 上一篇/下一篇導覽 */
.article-navigation {
  border-top: 2px solid #e0e0e0;
}

.nav-article-link {
  display: block;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  height: 100%;
}

.nav-article-link:hover {
  background: #e9ecef;
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.nav-label {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.nav-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.prev-article {
  text-align: left;
}

.next-article {
  text-align: right;
}

/* 延伸閱讀 */
.related-articles h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

.related-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.related-item {
  display: block;
  text-decoration: none;
  color: inherit;
  padding: 1.5rem 0;
  border-bottom: 1.5px solid #333;
  transition: all 0.3s ease;
}

.related-item:last-child {
  border-bottom: none;
}

.related-item:hover {
  background: #f8f9fa;
  padding-left: 1rem;
  padding-right: 1rem;
}

.related-item:hover .related-title {
  color: #007bff;
}

.related-item-content {
  padding: 0.5rem 0;
}

.related-badge-date {
  display: flex;
  align-items: center;
}

.related-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  transition: color 0.3s ease;
}

@media (max-width: 768px) {
  .post-container {
    padding-top: 120px;
    padding-bottom: 60px;
  }

  .article-title {
    font-size: 1.8rem;
  }
  
  .article-content {
    font-size: 1rem;
  }
  
  .article-content h2 {
    font-size: 1.5rem;
  }
  
  .article-content h3 {
    font-size: 1.3rem;
  }

  .nav-title {
    font-size: 1rem;
  }

  .related-title {
    font-size: 1.1rem;
  }
}
</style>
